'use client'

import {
  Avatar,
  ChatContainer,
  MainContainer,
  Message,
  MessageInput,
  MessageList,
  TypingIndicator,
} from '@chatscope/chat-ui-kit-react'
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
} from 'react'
import {
  safeGetItem,
  safeGetJSON,
  safeSetItem,
  safeRemoveItem,
} from '../helpers/safeStorage'
import { parseServerDate } from '../helpers/date'
import {
  FaCamera,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaEyeSlash,
  FaGem,
  FaGift,
  FaPause,
  FaPlay,
  FaPencilAlt,
  FaShare,
  FaSpinner,
  FaSync,
  FaTheaterMasks,
  FaTimes,
  FaTrash,
  FaComments,
  FaHeart,
  FaPhone,
  FaChevronDown,
  FaLock,
  FaSave,
  FaFilm,
  FaGlobeAmericas,
  FaArchive,
  FaCog,
} from 'react-icons/fa'

import { HiSpeakerWave } from 'react-icons/hi2'
import '../chatStyles.css'

import copy from 'copy-to-clipboard'
import { getIdToken } from 'firebase/auth'
import { t } from 'i18next'
import { isMobile } from 'react-device-detect'
import { BsChatDotsFill, BsPersonBoundingBox } from 'react-icons/bs'
import { FaDice } from 'react-icons/fa'
import { useVideoAutoplayPreference } from '../helpers/videoAutoplayPreference'
import {
  LTX_LENGTHS,
  LTX_RESOLUTION_DEFAULT,
  ltxGemsFor,
} from '../helpers/videoVersions'
import {
  MdDeleteSweep,
  MdMovie,
  MdPsychology,
  MdRecordVoiceOver,
} from 'react-icons/md'
import {
  TbCards,
  TbFaceId,
  TbPhotoSpark,
  TbUser,
  TbUsersGroup,
  TbUsersPlus,
} from 'react-icons/tb'
import { useRouter } from 'next/navigation'
import { ClipLoader } from 'react-spinners'
import Discover from '../component/Discover'
import Modal from '../component/Modal'
import { useAuth } from '../contexts/AuthContext'
import GiftShop, { GiftConfetti, useGiftsEnabled } from './GiftShop'
import { ChatCreateContext } from '../contexts/ChatCreateContext'
import { scenariolist, VOICES } from '../helpers/config'
import {
  capitalizeNames,
  getRandomOfflineReply,
  isSafeCountry,
  isUrl,
  processMessageContent,
  safeJSONParse,
  saveImage,
  selectOfflineSelfie,
  stripHTML,
  truncate,
} from '../helpers/helpers'
import ChatCard from './ChatCard'
import CreateCharacter from './CreateCharacter'
import MyPersona from './MyPersona'
import CreateGroup from './CreateGroup'
import GroupMemberSettings from './GroupMemberSettings'
import Link from 'next/link'
import VoiceRecorder from './VoiceRecorder'
import SignUpComponent from './SignUpComponent'
import { staticProfiles } from '../config/staticImages'
import { useTranslation } from 'react-i18next'
import { CreateContext } from '../contexts/CreateContext'
import { ChatContext } from '../contexts/ChatContext'
import OverlayActionsMenu from './OverlayActionsMenu'
import ImageLightbox from './ImageLightbox'
import * as accountsApi from '../lib/api/accounts.ts'
import { playableAudioUrl } from '../lib/audio-url'

// Plan tier as pricing sees it (mirrors myapp/chat_pricing.plan_tier): Studio
// is the plan users see as Pro, Max sits above it; an unknown paid plan gets
// Premium's numbers, never Free's.
const chatTier = (plan) => {
  if (!plan || plan === 'Free') return 'Free'
  if (plan === 'Pro' || plan === 'Studio' || plan === 'Max') return 'Pro'
  if (plan === 'Plus' || plan === 'Premium') return plan
  return 'Premium'
}
// Messages per 3-hour window for this plan on this model; null = unlimited.
const chatAllowance = (m, plan) =>
  m?.allowance ? m.allowance[chatTier(plan)] : undefined

const MODELS = [
  {
    code: '250-large',
    title: 'v2.5 Pro',
    description: 'Best RP, deeper immersion, better memory.',
    legacy: false,
    plan: 'Free',
    // Messages per 3-hour window by plan tier; null = unlimited (fair use).
    // Mirrors myapp/chat_pricing.py — tests/test_chat_allowance.py reads this
    // literal, keep the shape.
    allowance: { Free: 20, Plus: 40, Premium: 100, Pro: null },
    gems: 0.4,
    hide: false,
    endpoint: 'v2',
  },
  {
    code: '250-small',
    title: 'v2.5 Fast',
    description: 'Fast, smooth RP for everyday chats.',
    legacy: false,
    plan: 'Free',
    // 3x the Pro model's allowance: about a third of the cost per message.
    allowance: { Free: 60, Plus: 120, Premium: 300, Pro: null },
    gems: 0.2,
    hide: false,
    endpoint: 'v2',
  },
  {
    code: '210-thinking',
    title: 'v2.1 Thinking',
    description: 'Slower, reasoning-focused responses.',
    legacy: true,
    plan: 'Plus',
    gems: 1.2,
    hide: false,
    endpoint: 'v2',
  },
  // {
  //   code: 'legacy',
  //   title: 'v1.0',
  //   description: 'Older classic model.',
  //   legacy: true,
  //   plan: 'Plus',
  //   gems: 0.5,
  //   hide: true,
  //   endpoint: 'v1',
  // },
]

// Builds the default match a signed-out guest lands on, mirroring the modern
// character shape (bio, interests, scenario, first_message, …) and seeding the
// chat with her opening line so the conversation feels alive on first paint.
const buildDefaultOfflineMatch = () => {
  const base = staticProfiles?.[0]
  if (!base) return null

  const firstMessage =
    `*I'm sprawled across my bed scrolling my phone when your message lights up the screen. A slow smile tugs at my lips as I glance at your profile.* ` +
    `"Mmm, finally... someone interesting 😏 I'm ${base.name}. Tonight was starting to feel like such a waste. So — are you gonna keep me entertained, or what?"`

  return {
    ...base,
    bio: `${base.name} is a ${base.age}-year-old from ${base.location} with expensive taste and a little too much free time on her hands.`,
    interests: 'Fine dining, fashion, going out, being spoiled',
    emotions: 5,
    openness: 5,
    scenario: `You just matched with ${base.name}, a flirty ${base.location} girl who's bored tonight and looking for someone to spoil her and keep her entertained.`,
    first_message: firstMessage,
    story_mode: true,
    date_uploaded: new Date().toISOString(),
    chat_history: [
      {
        content: firstMessage,
        send_date: new Date().toISOString(),
        role: 'assistant',
      },
    ],
  }
}

// Groups are stored server-side as their own docs; adapt one into the same
// shape the chat list / selection / history code already expects for a 1:1
// "match", tagged with isGroup so a few call-sites render/route it as a group.
const toGroupMatch = (g) => ({
  ...g,
  isGroup: true,
  imageUrl: (g.participant_avatars || []).filter(Boolean),
  chat_history: g.chat_history || [],
})

// Overlapping mini-avatars for a group's list row / header.
const GroupAvatars = ({ avatars = [], size = 56 }) => {
  const shown = (avatars.length ? avatars : [null]).slice(0, 2)
  const inner = Math.round(size * 0.72)
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {shown.map((src, i) => (
        <img
          key={i}
          src={
            src ||
            'https://gravatar.com/avatar/f5862edde16dd6f7a1e30905b4096241?s=200&d=wavatar&r=x'
          }
          alt="Member"
          className="absolute rounded-full object-cover object-top border-2 border-[var(--middle-bg-color)]"
          style={{
            width: inner,
            height: inner,
            top: i === 0 ? 0 : 'auto',
            bottom: i === 0 ? 'auto' : 0,
            left: i === 0 ? 0 : 'auto',
            right: i === 0 ? 'auto' : 0,
            zIndex: shown.length - i,
          }}
        />
      ))}
    </div>
  )
}

// The emojis shown beside a character's name: unique gifts, ranked by price
// (most expensive first) and capped. Ranking by price rather than recency means
// the diamond ring keeps its slot when a box of chocolates arrives later — the
// shelf is meant to show the best of what she was given, not the latest.
const SHELF_MAX = 3

function topGiftEmojis(recentGifts, giftById, max = SHELF_MAX) {
  if (!Array.isArray(recentGifts) || !giftById) return []
  const seen = new Map()
  for (const g of recentGifts) {
    const entry = g && giftById[g.id]
    if (entry && !seen.has(g.id)) seen.set(g.id, entry)
  }
  return [...seen.values()]
    .sort((a, b) => (b.gems || 0) - (a.gems || 0))
    .slice(0, max)
    .map((g) => g.emoji)
}

const MessageButton = ({
  match,
  onLinkClick,
  selected,
  onClose,
  setCardModalOpen,
  giftById,
}) => {
  const name = capitalizeNames(match?.name) || 'Unknown'
  // Gifts A/B: the emojis of what she was actually given, beside her name. Only
  // real gifts are ever shown — never placeholders for ungifted items, which
  // would turn a keepsake into a set to complete and start pressuring spend.
  const shelf = topGiftEmojis(match?.recent_gifts, giftById)
  return (
    <div className="relative group ">
      <button
        onClick={onLinkClick}
        className={`w-full flex items-center px-4 rounded-md ${
          selected && 'bg-[var(--foreground-bg-color)] shadow-sm'
        }`}
      >
        <div className="flex-shrink-0 mr-1">
          {match?.isGroup ? (
            <GroupAvatars avatars={match?.imageUrl} size={56} />
          ) : (
            <img
              src={
                match?.imageUrl && match.imageUrl.length > 0
                  ? match.imageUrl[0]
                  : 'https://gravatar.com/avatar/f5862edde16dd6f7a1e30905b4096241?s=200&d=wavatar&r=x'
              }
              alt="Avatar"
              className="h-14 w-14 rounded-full object-cover object-top cursor-pointer hover:scale-[1.05] transition-all duration-300 ease-in-out"
              onClick={() => setCardModalOpen(true)}
            />
          )}
        </div>

        <div className="my-4 flex flex-col items-start">
          <div
            onClick={onLinkClick}
            className={`px-2 rounded-md cursor-pointer font-semibold`}
          >
            {name ?? 'Unknown'}
            {shelf.length > 0 && (
              <span
                className="pc-gift-shelf"
                title={`Gifts you've given ${name}`}
                aria-label={`${shelf.length} gift${shelf.length > 1 ? 's' : ''} given`}
              >
                {shelf.map((emoji, i) => (
                  <span key={i}>{emoji}</span>
                ))}
              </span>
            )}
          </div>
          <div
            onClick={onLinkClick}
            className={`px-2 rounded-md cursor-pointer text-gray-300 text-sm text-left`}
          >
            {match?.chat_history && match?.chat_history?.length > 0 ? (
              isUrl(
                match?.chat_history[match?.chat_history?.length - 1]?.content,
              ) ? (
                `${name} sent an Image`
              ) : (
                truncate(
                  match?.chat_history[match?.chat_history?.length - 1]
                    ?.content || '',
                  isMobile ? 10 : 15,
                )
              )
            ) : (
              <span className="text-bold text-[var(--main-bg-color)]">
                New Match!
              </span>
            )}{' '}
          </div>

          <div className="text-xs text-gray-600 px-2">
            {parseServerDate(
              match?.chat_history?.length > 0
                ? match.chat_history[match.chat_history.length - 1]
                    ?.send_date ||
                    match.chat_history[match.chat_history.length - 1]
                      ?.send_date ||
                    match.chat_history[match.chat_history.length - 1]?.timestamp
                : match?.date_uploaded,
            ).toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })}
          </div>
        </div>
      </button>
      {selected && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 transform translate-x-1/2 -translate-y-1/2 bg-white p-1 rounded-full shadow "
        >
          <FaTimes className="h-4 w-4 text-gray-600" />
        </button>
      )}
    </div>
  )
}

// What "create image from scene" actually costs, so the button can say so before it is tapped.
// Mirrors generate_bp: 1 (batch) + character_cost, where char_version v3 (Qwen apply_character)
// costs 3 and the older IPAdapter face-swap costs 1. Ultra quality and non-XL models add 0 on
// this path.
//
// GROUPS ARE NOT KEYED ON THE PREFERENCE. /api/generate_group_image always renders through
// apply_two_characters with charVersion "v3", so a group image costs 4 whatever the 1:1
// aiCharacterVersion says — quoting the preference there would under-state the price for
// anyone on the face-swap path.
const CHAT_IMAGE_GEM_COST = { v3: 2, default: 4 }
const GROUP_IMAGE_GEM_COST = 4

const chatImageGemCost = (isGroup, aiCharacterVersion) =>
  isGroup
    ? GROUP_IMAGE_GEM_COST
    : (CHAT_IMAGE_GEM_COST[aiCharacterVersion] ?? CHAT_IMAGE_GEM_COST.default)

// Chat video settings. Only the two knobs the panel exposes — length and quality — and both
// read their values from the shared LTX constants rather than a second copy here. Everything
// else is fixed for chat: Portrait (a chat thread is a phone-shaped column, so Wide would just
// letterbox), 24fps (the 48fps surcharge buys smoothness nobody notices in a short clip and
// burns GPU the pool cannot spare) and Auto camera.
const CHAT_VIDEO_FPS = 24
const CHAT_VIDEO_ASPECT = 'Portrait'
// Quality is fixed at the Fast tier: a chat clip plays in a ~300px column, where the High
// tier's larger render is invisible — while costing ~2.4x the GPU on a pool with only a few
// hundred jobs/day of headroom, and +13/+22 gems the user gets nothing visible for.
const CHAT_VIDEO_RESOLUTION = LTX_RESOLUTION_DEFAULT
// 6s is the default clip: it is the cheapest (18 gems vs 30), the fastest on a pool with
// little headroom, and long enough for the median chat line — the longest quoted sentence
// in a roleplay reply measures ~10 words against a 14-word budget at 6s.
const CHAT_VIDEO_LENGTH_DEFAULT = 6

const ChatComponent = () => {
  const {
    user,
    subscription,
    gems,
    setGems,
    chatAllowance: chatAllowanceRemaining,
    setChatAllowance: setChatAllowanceRemaining,
    safe,
    displayPlan,
    isPlusOrHigher,
    isStudioOrHigher,
    displayProfilePicture,
    defaultStoryMode,
  } = useAuth()

  // Companion gifts A/B. The hook self-gates: `enabled` is false for the control
  // arm and for any account predating the experiment, so every gift UI below is
  // simply absent for them and the chat renders exactly as it did before.
  const [giftShopOpen, setGiftShopOpen] = useState(false)
  // One-shot celebration after a gift lands. Mounted only while active, so the
  // canvas costs nothing the rest of the time.
  const [giftCelebration, setGiftCelebration] = useState(null)
  const [clearGiftsModalOpen, setClearGiftsModalOpen] = useState(false)
  const [clearingGifts, setClearingGifts] = useState(false)
  const {
    enabled: giftsEnabled,
    gifts: giftCatalog,
    sections: giftSections,
    gems: giftGems,
    refresh: refreshGifts,
  } = useGiftsEnabled(user)

  // gift id -> emoji. recent_gifts on the character doc stores id/name/gems but
  // not the emoji, so it is resolved from the catalog the shop already fetched.
  // Full catalog entry by id. The shelves rank by price, not recency, so a
  // diamond ring is never pushed out of view by a later box of chocolates.
  const giftById = useMemo(() => {
    const m = {}
    for (const g of giftCatalog || []) m[g.id] = g
    return m
  }, [giftCatalog])

  // Every emoji that can appear as a gift message, so a sent gift can be told
  // apart from someone simply typing an emoji.
  const giftEmojiSet = useMemo(
    () => new Set((giftCatalog || []).map((g) => g.emoji)),
    [giftCatalog],
  )

  // emoji -> tier, so a gift bubble is tinted the same as its shop tile.
  const giftTierByEmoji = useMemo(() => {
    const m = {}
    for (const g of giftCatalog || []) m[g.emoji] = g.tier
    return m
  }, [giftCatalog])

  const matchesBoxRef = useRef(null)

  const router = useRouter()
  const safeCountry = isSafeCountry()
  const { i18n } = useTranslation()

  const [isBotTyping, setIsBotTyping] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [matchesLoading, setMatchesLoading] = useState(false)

  const [sortedMatches, setSortedMatches] = useState([])
  const [currentChat, setCurrentChat] = useState([])

  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false)
  const [limitTitle, setLimitTitle] = useState(null)
  // Confirm-button label for the limit modal; null = the generic "Upgrade &
  // Unlock Benefits". The chat paywall sets a plan-specific one.
  const [limitConfirmation, setLimitConfirmation] = useState(null)
  // Whether this session has already seen the "free messages used up" pitch.
  // Once is a nudge; every charged turn would be a nag.
  // Per model code: the user has been told this window's messages now cost gems.
  const chatOutAcked = useRef({})
  // The quiet "free messages used" line over the input (2026-10-06): shown for a few seconds.
  const [chatOutNotice, setChatOutNotice] = useState(null)
  const chatOutNoticeTimer = useRef(null)
  const showChatOutNotice = (text) => {
    clearTimeout(chatOutNoticeTimer.current)
    setChatOutNotice(text)
    chatOutNoticeTimer.current = setTimeout(() => setChatOutNotice(null), 6000)
  }
  const closeLimitModal = () => {
    setIsLimitModalOpen(false)
    setLimitConfirmation(null)
  }
  const [errorText, setErrorText] = useState('')
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)

  const [upgradeText, setUpgradeText] = useState('')

  const [voiceModalOpen, setVoiceModalOpen] = useState(false)
  const [, setAccentModalOpen] = useState(false)

  const [isEditScenarioLoading, setIsEditScenarioLoading] = useState(false)
  const [isEditPersonalityLoading, setIsEditPersonalityLoading] =
    useState(false)
  const [roleplayModalOpen, setRoleplayModalOpen] = useState(false)
  const [scenarioModalOpen, setScenarioModalOpen] = useState(false)
  const [cardModalOpen, setCardModalOpen] = useState(false)

  const [newScenario, setNewScenario] = useState('')
  const [newPersonality, setNewPersonality] = useState('')
  const [currentSuggestion, setCurrentSuggestion] = useState('')
  const [message, setMessage] = useState('')
  const [discover, setDiscover] = useState(false)
  const [, setAudioReply] = useState()
  const [, setSelfieReply] = useState()

  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [publishTarget, setPublishTarget] = useState(null)
  const [acceptedPublishTerms, setAcceptedPublishTerms] = useState(false)
  const [showTermsError, setShowTermsError] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  // Public vs Private for the chat publish modal (parity with /generate). Defaults
  // to Private — chat images are personal, so publishing to the public profile is opt-in.
  const [publishVisibility, setPublishVisibility] = useState('Private')

  const [chatArchiveLoading, setChatArchiveLoading] = useState(false)
  const [chatArchive, setChatArchive] = useState([])
  const [chatArchiveModalOpen, setChatArchiveModalOpen] = useState(false)
  const [chatSettingsModalOpen, setChatSettingsModalOpen] = useState(false)

  const [deleteAllProfilesModalOpen, setDeleteAllProfilesModalOpen] =
    useState(false)
  const [confirmDeleteAllModalOpen, setConfirmDeleteAllModalOpen] =
    useState(false)

  const [modelDropdownOpen, setModelDropdownOpen] = useState(false)
  const [model, setModel] = useState(MODELS[0])

  const [signUpModalOpen, setSignUpModalOpen] = useState(false)
  // Guest teaser gate: session-scoped (not persisted) so every visit grants one
  // free exchange before the blocking sign-up modal appears.
  const [hasSentOfflineMessage, setHasSentOfflineMessage] = useState(false)
  const [showSignUpGate, setShowSignUpGate] = useState(false)

  // safeGetJSON (not safeJSONParse): JSON.parse(null) returns null WITHOUT throwing,
  // so the old safeJSONParse(storedValue, true) gave fresh users null — audio/selfies
  // silently defaulted OFF instead of the intended true, and the persist effect then
  // stored the literal string "null", making it sticky. safeGetJSON maps both a
  // missing key and a parsed null to the fallback, repairing already-poisoned keys.
  const [audioMessagesEnabled, setAudioMessagesEnabled] = useState(() =>
    safeGetJSON('audioMessagesEnabled', true),
  )

  const [selfiePhotosEnabled] = useState(() =>
    safeGetJSON('selfiePhotosEnabled', true),
  )

  useEffect(() => {
    safeSetItem('audioMessagesEnabled', JSON.stringify(audioMessagesEnabled))
  }, [audioMessagesEnabled])

  useEffect(() => {
    safeSetItem('selfiePhotosEnabled', JSON.stringify(selfiePhotosEnabled))
  }, [selfiePhotosEnabled])

  // Which AI Character model applies the character to "Create image from scene"
  // images: 'v4' (Qwen apply_character) or 'v3' (IPAdapter FaceID).
  // Persisted locally; picks the /api/generate_image_roleplay_v3|v4 endpoint.
  const [aiCharacterVersion, setAiCharacterVersion] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = safeGetItem('aiCharacterVersion')
      return stored === 'v3' || stored === 'v4' ? stored : 'v4'
    }
    return 'v4'
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      safeSetItem('aiCharacterVersion', aiCharacterVersion)
    }
  }, [aiCharacterVersion])

  const [modalOpen, setModalOpen] = useState(false)
  const [clearModalOpen, setClearModalOpen] = useState(false)
  const [scenario, setScenario] = useState('')
  const [isEditingScenario, setIsEditingScenario] = useState(false)
  const [personality, setPersonality] = useState('')
  const [isEditingPersonality, setIsEditingPersonality] = useState(false)

  // Inline message edit: the user message being edited and its draft text.
  const [editingMessage, setEditingMessage] = useState(null)
  const [editText, setEditText] = useState('')

  // The chat image whose overlay actions menu is open (keeps its overlay visible).
  const [openImageMenuMsg, setOpenImageMenuMsg] = useState(null)

  // Full-screen image viewer source (mirrors /generate); null when closed.
  const [fullSrc, setFullSrc] = useState(null)

  const [isRoleplay, setIsRoleplay] = useState(true)
  const [isToggleRoleplayLoading, setIsToggleRoleplayLoading] = useState(false)
  const [isNarrationLoading, setIsNarrationLoading] = useState(false)
  const [currentLoadingNarration, setCurrentLoadingNarration] = useState('')

  const [isImageLoading, setIsImageLoading] = useState(false)
  // Chat video. Length/quality are USER preferences (persisted like the other chat prefs);
  // the character's voice is not one of them — video inherits the voice already chosen for
  // the character in this same panel, so one character keeps one voice.
  // Reuses the existing per-device autoplay preference (Settings -> "Autoplay videos"),
  // so a phone on cellular that has it off does not start playing chat clips either.
  const chatVideoAutoplay = useVideoAutoplayPreference()
  const [isVideoLoading, setIsVideoLoading] = useState(false)
  const [videoStatusText, setVideoStatusText] = useState('')
  const [chatVideoLength, setChatVideoLength] = useState(() => {
    if (typeof window === 'undefined') return CHAT_VIDEO_LENGTH_DEFAULT
    // One-time reset to the 6s default. The value persisted during development, so
    // anyone who tried 10s while testing would otherwise keep paying 30 gems a clip
    // without having chosen it. Runs once per browser; a deliberate choice made after
    // this sticks.
    if (safeGetItem('chatVideoLengthReset6s') !== 'done') {
      safeSetItem('chatVideoLengthReset6s', 'done')
      safeSetItem('chatVideoLength', String(CHAT_VIDEO_LENGTH_DEFAULT))
      return CHAT_VIDEO_LENGTH_DEFAULT
    }
    const stored = Number(safeGetItem('chatVideoLength'))
    return LTX_LENGTHS.some((l) => l.value === stored)
      ? stored
      : CHAT_VIDEO_LENGTH_DEFAULT
  })

  const [, setIsShareLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [personaModalOpen, setPersonaModalOpen] = useState(false)
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  // In a group, the name of the character currently "thinking" (drives the
  // typing indicator so it reads "Alice is thinking…" then "Bob is thinking…").
  const [typingName, setTypingName] = useState(null)
  // Staged photo (compressed JPEG data URL) waiting to be sent with the next message.
  const [pendingPhoto, setPendingPhoto] = useState(null)
  // On mobile, tapping the paperclip opens this chooser (camera vs gallery).
  const [photoSourceModalOpen, setPhotoSourceModalOpen] = useState(false)
  const photoInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const [profileImage, setProfileImage] = useState()
  const [currentSentDate, setCurrentSentDate] = useState()

  const [callModalOpen, setCallModalOpen] = useState(false)
  const [legacyOpen, setLegacyOpen] = useState(false)

  const {
    matches,
    setMatches,
    currentMatch,
    setCurrentMatch,
    matchesFetchedForUid,
    setMatchesFetchedForUid,
  } = useContext(ChatContext)

  // Emojis of the gifts given to the character currently open, for the
  // clear/unmatch dialogs.
  //
  // MUST stay below the ChatContext destructure: a useMemo dependency array is
  // evaluated immediately during render, so referencing `currentMatch` above its
  // own declaration is a temporal-dead-zone ReferenceError that takes the whole
  // chat down via the error boundary.
  const handleClearGifts = async () => {
    if (!user || !currentMatch?.id || clearingGifts) return
    setClearingGifts(true)
    try {
      const idToken = await getIdToken(user)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/chat/gifts/clear`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({ character_id: currentMatch.id }),
        },
      )
      if (!res.ok) throw new Error('clear failed')
      // Drop the shelf locally so the UI reflects it without a refetch.
      setMatches((prev) =>
        prev.map((m) =>
          m.id === currentMatch.id ? { ...m, recent_gifts: [] } : m,
        ),
      )
      setSortedMatches((prev) =>
        prev.map((m) =>
          m.id === currentMatch.id ? { ...m, recent_gifts: [] } : m,
        ),
      )
      setCurrentMatch({ ...currentMatch, recent_gifts: [] })
      refreshGifts()
    } catch {
      setErrorText(t('somethingWentWrong'))
      setIsErrorModalOpen(true)
    } finally {
      setClearingGifts(false)
      setClearGiftsModalOpen(false)
    }
  }

  // Refetch the catalog scoped to THIS character when the shop opens: the After
  // dark shelf is gated on her own openness, so a modest companion must not be
  // offered it. Keyed on open rather than on every match change to keep the
  // request off the chat's hot path.
  //
  // NOTE this must stay BELOW the ChatContext destructure above — `currentMatch`
  // is declared there, and referencing it from a dependency array any earlier is
  // a TDZ ReferenceError that throws on every render (it took the whole chat
  // down once already).
  useEffect(() => {
    if (giftShopOpen && currentMatch?.id) refreshGifts(currentMatch.id)
  }, [giftShopOpen, currentMatch?.id, refreshGifts])

  // Full entries (not just emojis) for the shop's "already given" cabinet.
  const ownedGifts = useMemo(() => {
    if (!giftsEnabled || currentMatch?.isGroup) return []
    const recent = currentMatch?.recent_gifts
    if (!Array.isArray(recent)) return []
    // Group repeats into a single entry with a count — three separate teddy
    // bear chips read as clutter, "a soft teddy bear x2" reads as a collection.
    // First-appearance order keeps it newest-first.
    const byId = new Map()
    for (const g of recent) {
      if (!g || !giftById[g.id]) continue
      const found = byId.get(g.id)
      if (found) found.count += 1
      else
        byId.set(g.id, {
          id: g.id,
          name: g.name,
          emoji: giftById[g.id].emoji,
          count: 1,
        })
    }
    return [...byId.values()]
  }, [giftsEnabled, currentMatch, giftById])

  const currentShelf = useMemo(() => {
    if (!giftsEnabled || currentMatch?.isGroup) return []
    const recent = currentMatch?.recent_gifts
    if (!Array.isArray(recent)) return []
    // Unique gifts only (repeats add no information in a narrow row), ranked
    // by price so the best thing she was given always shows.
    return topGiftEmojis(recent, giftById)
  }, [giftsEnabled, currentMatch, giftById])

  // Live selection for in-flight fetchMatches calls, which would otherwise
  // preserve a stale closure value when refreshing in the background.
  const currentMatchRef = useRef(null)
  useEffect(() => {
    currentMatchRef.current = currentMatch
  }, [currentMatch])

  const {
    setCharImage,
    setActiveTab,
    setFaceTab,
    setChatId,
    setChatImageModel,
  } = useContext(ChatCreateContext)

  const {
    setCharImage: setCharImageFromCreate,
    lora,
    prompt,
    imageSize,
    model: createModel,
    setVideoRefImage,
    setVideoRefId,
    setVideoRefLora,
    setVideoPrompt,
    setVideoModel,
    setVideoImageSize,
    setFormatTab,
    setVideoId,
    setVideoStartingPoint,
  } = useContext(CreateContext)

  let audio = null

  useEffect(() => {
    const chatImage = safeGetItem('chatImage')
    const chatId = safeGetItem('chatId')

    if (chatImage) {
      setCharImage(chatImage)
      safeRemoveItem('chatImage')
      setCreateModalOpen(true)
      setActiveTab(0)
      setFaceTab('existing')
    }

    if (chatId) {
      setChatId(chatId)
      safeRemoveItem('chatId')
    }

    const chatImageModel = safeGetItem('chatImageModel')
    if (chatImageModel !== null) {
      setChatImageModel(chatImageModel)
      safeRemoveItem('chatImageModel')
    }
  }, [])

  useEffect(() => {
    const fetchProfileImage = async () => {
      if (!user) return

      try {
        const idToken = await getIdToken(user)
        const response = await accountsApi.getProfile(idToken)

        if (!response.ok) {
          throw new Error('Response not ok')
        }

        const data = await response.json()
        setProfileImage(data.profile_image_url)
      } catch (error) {
        console.error('Something went wrong fetching the profile image.', error)
      }
    }

    fetchProfileImage()
  }, [user])

  const getRandomScenario = () => {
    const randomIndex = Math.floor(Math.random() * scenariolist.length)
    return scenariolist[randomIndex] // Get one random item
  }

  useEffect(() => {
    setCurrentSuggestion(getRandomScenario())
  }, [])

  const refreshSuggestion = () => {
    setCurrentSuggestion(getRandomScenario())
  }

  const addSuggestionToPrompt = () => {
    setNewScenario(currentSuggestion)
    refreshSuggestion() // Refresh suggestion after adding it
  }

  useEffect(() => {
    // A Free account on a Plus-only model falls back to v2.5 Fast. It used to fall back to
    // 210-small, which ran on a model Groq no longer serves; 210-small and 210-large were
    // retired on 2026-09-28 (owner decision).
    const applyFreeModel = async () => {
      if (model?.plan === 'Plus' && displayPlan === 'Free') {
        setModel(MODELS.find((m) => m.code === '250-small'))
      }
    }

    applyFreeModel()
  }, [model?.plan, displayPlan])

  useEffect(() => {
    if (user) {
      safeRemoveItem('offlineCharacter')
      safeRemoveItem('offlineMatches')
      safeRemoveItem('hasSentOfflineMessage')
      return
    }

    // Load offlineMatches from localStorage
    const storedOfflineMatches = safeGetItem('offlineMatches')
    let offlineMatches = safeJSONParse(storedOfflineMatches, [])
    if (!Array.isArray(offlineMatches)) offlineMatches = []

    // Merge in a freshly-landed offlineCharacter, if any.
    const offlineCharacterStr = safeGetItem('offlineCharacter')
    const offlineCharacter = offlineCharacterStr
      ? safeJSONParse(offlineCharacterStr, null)
      : null

    if (offlineCharacter) {
      const isAlreadyInMatches = offlineMatches.some(
        (match) => match.id === offlineCharacter.id,
      )
      if (!isAlreadyInMatches) {
        offlineMatches = [...offlineMatches, offlineCharacter]
      }
      safeSetItem('offlineMatches', JSON.stringify(offlineMatches))
      safeRemoveItem('offlineCharacter')
    }

    // Guarantee a default match so the guest can start chatting immediately,
    // even if they navigated straight to /chat with nothing stored. Also
    // upgrade a stale auto-seeded default (from before first_message existed)
    // so returning guests still get the modern greeting.
    const defaultId = staticProfiles?.[0]?.id
    const staleDefaultIdx = offlineMatches.findIndex(
      (m) => m.id === defaultId && !m.first_message,
    )

    if (offlineMatches.length === 0) {
      const defaultMatch = buildDefaultOfflineMatch()
      if (defaultMatch) {
        offlineMatches = [defaultMatch]
        safeSetItem('offlineMatches', JSON.stringify(offlineMatches))
      }
    } else if (staleDefaultIdx !== -1) {
      const defaultMatch = buildDefaultOfflineMatch()
      if (defaultMatch) {
        offlineMatches = offlineMatches.map((m, i) =>
          i === staleDefaultIdx ? defaultMatch : m,
        )
        safeSetItem('offlineMatches', JSON.stringify(offlineMatches))
      }
    }

    setMatches(offlineMatches)
    // Guest matches must never pass for a fetched cache: a login right after
    // guesting has to block on its first real fetch, not chat against them.
    setMatchesFetchedForUid(null)

    // Auto-select a character so the guest can start chatting right away,
    // preferring the one they just landed on.
    if (offlineMatches.length > 0) {
      const selected =
        (offlineCharacter &&
          offlineMatches.find((m) => m.id === offlineCharacter.id)) ||
        sortMatches(offlineMatches)[0]
      setCurrentMatch(selected)
      setDiscover(false)
    }
  }, [user]) // This effect depends on the user state

  // For a group, resolve which participant's avatar to show for an assistant
  // message from its `sender` (character id). Falls back to the first member.
  const groupSenderAvatar = (senderId) => {
    if (!currentMatch?.isGroup) return currentMatch?.imageUrl?.[0]
    const idx = (currentMatch.participants || []).indexOf(senderId)
    return (
      (idx >= 0 && currentMatch.participant_avatars?.[idx]) ||
      currentMatch?.imageUrl?.[0]
    )
  }

  // Reveal group replies one at a time — typing indicator + a short pause
  // between each — so the two characters feel like they answer in turn (the
  // second reacting to the first), not in unison.
  const revealGroupReplies = async (replies, targetId) => {
    for (let i = 0; i < replies.length; i++) {
      // Show whose turn it is — "{name} is thinking…" — with a longer,
      // human-feeling pause before the second character chimes in.
      setTypingName(replies[i]?.sender_name || null)
      setIsBotTyping(true)
      await new Promise((resolve) =>
        setTimeout(
          resolve,
          i === 0 ? 500 + Math.random() * 400 : 1500 + Math.random() * 900,
        ),
      )
      setIsBotTyping(false)
      const reply = replies[i]
      setMatches((prev) =>
        prev?.map((m) =>
          m.id === targetId
            ? { ...m, chat_history: [...(m.chat_history || []), reply] }
            : m,
        ),
      )
    }
    setTypingName(null)
  }

  // "Let them keep talking" — run another group round with no user message, so
  // the characters react to each other. The backend caps how far it runs.
  const continueGroup = async () => {
    if (!user || !currentMatch?.isGroup || isLoading) return
    setIsLoading(true)
    setIsBotTyping(true)
    setTypingName(currentMatch?.participant_names?.[0] || currentMatch?.name)
    try {
      const idToken = await getIdToken(user)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/generate_group_chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            groupId: currentMatch.id,
            continuation: true,
            isRoleplay: isRoleplay,
            language: i18n?.language,
            version: model?.code,
          }),
        },
      )
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        const replies = Array.isArray(data?.messages) ? data.messages : []
        await revealGroupReplies(replies, currentMatch.id)
      } else {
        setErrorText(data?.error || 'Something went wrong. Please try again.')
        setIsErrorModalOpen(true)
      }
    } catch (e) {
      console.error('Failed to continue group:', e)
    } finally {
      setIsBotTyping(false)
      setIsLoading(false)
      setTypingName(null)
    }
  }

  // Patch a character's fields in the list after a per-member group edit
  // (personality/voice). The group re-derives from user_profiles server-side, so
  // this just keeps the settings UI (and any 1:1 chat) in sync locally.
  const updateCharacterInMatches = (id, patch) => {
    setMatches((prev) =>
      prev?.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    )
  }

  // A new group was created: drop its synthetic match into the list and open it.
  const handleGroupCreated = (group) => {
    if (!group) return
    const synthetic = toGroupMatch(group)
    // Replace any existing entry with the same id (the backend returns the
    // existing group when one already exists for these characters).
    setMatches((prev) => [
      synthetic,
      ...(prev || []).filter((m) => m.id !== synthetic.id),
    ])
    setCurrentMatch(synthetic)
    setDiscover(false)
    setGroupModalOpen(false)
  }

  const fetchMatches = async () => {
    if (!user) return

    // Matches already fetched for this account this session (and kept fresh by
    // the chat flow): revalidate in the background instead of hiding the page
    // behind the blocking loader.
    const isRevalidate = matchesFetchedForUid === user.uid

    try {
      if (!isRevalidate) setMatchesLoading(true)

      const idToken = await getIdToken(user)

      const authHeaders = {
        'Content-Type': 'application/json',
        Authorization: idToken,
      }

      // Fetch 1:1 chats and group chats together. Groups are modelled as
      // synthetic "matches" (isGroup) so the entire list/sort/selection/history
      // plumbing below works on them unchanged — only a few spots branch on it.
      const [response, groupsResponse] = await Promise.all([
        fetch(`${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_chats`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({}),
        }),
        fetch(`${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_groups`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({}),
        }).catch(() => null),
      ])

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(data?.error || 'Response not ok')
      }

      const groupsData = groupsResponse
        ? await groupsResponse.json().catch(() => ({}))
        : {}
      const groupMatches = (
        Array.isArray(groupsData?.groups) ? groupsData.groups : []
      ).map(toGroupMatch)

      const fetchedMatches = [
        ...(Array.isArray(data?.matches) ? data.matches : []),
        ...groupMatches,
      ]
      const sorted = sortMatches(fetchedMatches)

      setMatches(fetchedMatches)
      setSortedMatches(sorted)
      setMatchesFetchedForUid(user.uid)

      // The character the user just tapped "Chat" on (Discover's handleMatch, the share
      // page) or created (CreateCharacter). Until 2026-10-04 this key was written but
      // never read, so tapping a character they had ALREADY matched — match_profile 409s,
      // nothing new joins the list — landed them on whichever chat was most recent
      // instead. One-shot: consumed on the first successful fetch so later visits to
      // /chat don't keep jumping back to it. It outranks the kept selection below
      // because it is an explicit request made just before this load.
      const requestedId = safeGetItem('characterId')
      if (requestedId !== null) safeRemoveItem('characterId')

      // "Discover characters" deep-link (e.g. the Plus-gated chat-from-image modal
      // on /generate): land on the browse view instead of auto-opening a chat.
      // Checked after matches settle so it can't be overridden by auto-selection.
      if (safeGetItem('openDiscover')) {
        safeRemoveItem('openDiscover')
        setCurrentMatch(null)
        setCurrentChat([])
        setDiscover(true)
        safeRemoveItem('currentMatch')
        return
      }

      if (sorted.length === 0) {
        setCurrentMatch(null)
        setCurrentChat([])
        setDiscover(true)
        safeRemoveItem('currentMatch')
        return
      }

      // /chat opens on Discover (product owner, 2026-10-05): landing straight in the
      // most recent chat meant Discover (and the Stories row at its top) was only ever
      // seen by people with no matches. A chat opens only when it was asked for — the
      // character just tapped or created (requestedId) — or is already on screen
      // (a background revalidation keeps the current selection and never jumps).
      // Everyone else's chats stay one tap away in the Messages list.
      const nextMatch =
        (requestedId && sorted.find((m) => String(m.id) === requestedId)) ||
        sorted.find((m) => m.id === currentMatchRef.current?.id) ||
        null

      // Nothing asked for and nothing (still) on screen: Discover. Also on a revalidation —
      // a return to /chat remounts with discover=false, and an unmatched open character is
      // no longer in `sorted`; both used to leave an empty chat pane with no way out.
      if (!nextMatch) {
        setCurrentMatch(null)
        setCurrentChat([])
        setDiscover(true)
        safeRemoveItem('currentMatch')
        return
      }

      setCurrentMatch(nextMatch)
      setDiscover(false)
      safeSetItem('currentMatch', JSON.stringify(nextMatch))
    } catch (error) {
      console.error('Something went wrong fetching matches.', error)

      // A failed background revalidation keeps the working cached state on
      // screen; only a failed blocking load falls back to the discover view.
      if (!isRevalidate) {
        setMatches([])
        setSortedMatches([])
        setCurrentMatch(null)
        setCurrentChat([])
        setDiscover(true)
        safeRemoveItem('currentMatch')
      }
    } finally {
      setMatchesLoading(false)
    }
  }

  const fetchChatArchive = async (characterId) => {
    if (!user || !characterId) return
    // Groups keep their own archive; the 1:1 archive endpoint 404s on a group id.
    if (currentMatch?.isGroup) return

    try {
      setChatArchiveLoading(true)

      const idToken = await getIdToken(user)

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_chat_archive`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({ characterId }),
        },
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data?.error || 'Response not ok')
      }

      setChatArchive(data.chat_history || [])
    } catch (error) {
      console.error('Failed to fetch full chat history.', error)
      setChatArchive([])
    } finally {
      setChatArchiveLoading(false)
    }
  }

  useEffect(() => {
    if (!callModalOpen && user) {
      fetchMatches()
    }
  }, [callModalOpen, user])

  const useCharacter = async () => {
    try {
      setCharImageFromCreate(
        currentMatch?.portraitImage ?? currentMatch?.imageUrl?.[0],
      )
      router.push('/generate')
    } catch (error) {
      console.log(error)
    }
  }

  const playAudio = (url) => {
    if (!url) {
      console.error('No URL provided for audio playback')
      return
    }

    const src = playableAudioUrl(url)
    if (!audio) {
      audio = new Audio(src)
    } else {
      audio.src = src
    }

    audio.play().catch((error) => {
      console.error('Failed to play audio:', error)
    })
  }

  const pauseAudio = () => {
    if (audio) {
      audio.pause()
    }
  }

  const handleUnmatch = async (id) => {
    if (typeof window === 'undefined') return

    if (!user) {
      router.push('/signup')
    }
    try {
      const idToken = await getIdToken(user)
      // Groups live in a different collection — delete the group, don't unmatch.
      const isGroup = matches?.find((m) => m.id === id)?.isGroup
      const endpoint = isGroup ? 'delete_group' : 'unmatch_profile'
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            id: id,
          }),
        },
      )

      await response.json()

      if (response.ok) {
        fetchMatches()
      } else {
        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
    }
  }

  const handleDeleteAllProfiles = async () => {
    if (typeof window === 'undefined') return

    if (!user) {
      router.push('/signup')
      return
    }

    try {
      const idToken = await getIdToken(user)

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/delete_user_profiles`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
        },
      )

      const data = await response.json()

      if (response.ok) {
        setDeleteAllProfilesModalOpen(false)
        fetchMatches()
      } else {
        throw new Error(data?.error || 'Failed to delete all profiles')
      }
    } catch (error) {
      console.error('Failed to delete all profiles.', error)
    }
  }

  const handleClear = async (id) => {
    if (typeof window === 'undefined') return

    if (!user) {
      router.push('/signup')
    }
    try {
      const idToken = await getIdToken(user)
      // Groups clear via their own endpoint (different Firestore collection).
      const isGroup = matches?.find((m) => m.id === id)?.isGroup
      const endpoint = isGroup ? 'clear_group_chat' : 'clear_chat'
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            id: id,
          }),
        },
      )

      await response.json()

      if (response.ok) {
        fetchMatches()
      } else {
        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
    }
  }

  // Persist a truncation of the current chat back to `index` (drops that message
  // and everything after it). Guests have no server-side chat, so the optimistic
  // local truncation in handleSubmitEdit is all they need.
  const rollbackChat = async (index, sendDate) => {
    if (!user) return

    const idToken = await getIdToken(user)
    // Groups live in a separate collection, so they need the group-aware
    // rollback endpoint; both accept the same { id, index, send_date } body.
    const rollbackEndpoint = currentMatch?.isGroup
      ? 'rollback_group_chat'
      : 'rollback_chat'
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/${rollbackEndpoint}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: idToken,
        },
        body: JSON.stringify({
          id: currentMatch?.id,
          index,
          send_date: sendDate,
        }),
      },
    )

    if (!response.ok) {
      throw new Error('Failed to roll back chat')
    }
  }

  // Roll the conversation back to a user message and re-send the corrected text,
  // which regenerates the reply against the truncated history.
  const handleSubmitEdit = async (msg) => {
    const trimmed = (editText || '').trim()
    if (!trimmed || isLoading) return

    const chatHistory =
      matches?.find((m) => m?.id === currentMatch?.id)?.chat_history || []

    // indexOf works because currentChat shares object refs with chat_history;
    // fall back to send_date + content if the reference was replaced.
    let realIndex = chatHistory.indexOf(msg)
    if (realIndex === -1) {
      realIndex = chatHistory.findIndex(
        (m) => m.send_date === msg.send_date && m.content === msg.content,
      )
    }
    if (realIndex === -1 || chatHistory[realIndex]?.role !== 'user') return

    // Optimistically roll the UI back immediately.
    setMatches((prevMatches) =>
      prevMatches?.map((match) =>
        match.id === currentMatch?.id
          ? { ...match, chat_history: chatHistory.slice(0, realIndex) }
          : match,
      ),
    )

    try {
      await rollbackChat(realIndex, msg.send_date)
    } catch (error) {
      console.error('Failed to roll back chat.', error)
      // Resync from the server rather than leave a bad optimistic state.
      fetchMatches()
      return
    }

    setEditingMessage(null)
    setEditText('')

    sendMessage(trimmed)
  }

  // Remove a single generated chat image from the conversation. These images live
  // only in client state (the roleplay-image endpoints never persist them to
  // chat_history), so dropping it from matches is all that's needed.
  const handleDeleteChatImage = (msg) => {
    setMatches((prevMatches) =>
      prevMatches?.map((match) =>
        match.id === currentMatch?.id
          ? {
              ...match,
              chat_history: (match.chat_history || []).filter((m) => m !== msg),
            }
          : match,
      ),
    )
  }

  const toggleRoleplay = async () => {
    // Groups keep their creation-time mode (no 1:1 toggle endpoint for them).
    if (currentMatch?.isGroup) return
    if (!user) {
      router.push('/signup')
      return
    }
    if (isToggleRoleplayLoading) {
      return
    }
    setIsToggleRoleplayLoading(true)

    try {
      const idToken = await getIdToken(user)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/toggle_story_mode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            id: currentMatch?.id,
          }),
        },
      )

      const data = await response.json()

      if (response.ok) {
        setIsRoleplay(data.new_value)

        // Update the matches state to reflect the change in story_mode for the updated match
        setMatches((prevMatches) =>
          prevMatches?.map((match) => {
            if (match.id === currentMatch?.id) {
              return { ...match, story_mode: data.new_value }
            }
            return match
          }),
        )
      } else {
        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
    } finally {
      setIsToggleRoleplayLoading(false)
    }
  }

  const shareChat = async () => {
    if (!user) {
      return
    }
    // Sharing/archiving are 1:1 features (they key off a user_profiles id).
    if (currentMatch?.isGroup) return
    if (!currentMatch?.id) {
      alert('Invalid image ID.')
      return
    }
    setIsShareLoading(true)

    try {
      const idToken = await user.getIdToken()
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/share_chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            image_id: currentMatch?.id,
          }),
        },
      )

      const data = await response.json()

      if (response.ok) {
        if (typeof window === 'undefined') return
        const baseUrl = window.location.origin
        const fullShareLink = `${baseUrl}/share/${data.share_link}`

        copy(fullShareLink)
        setCopied(true)
      } else {
        throw new Error(data.error || 'Failed to share chat')
      }
    } catch (error) {
      console.error('Something went wrong:', error)
      alert(error.message)
    } finally {
      setIsShareLoading(false)
    }
  }

  useEffect(() => {
    let timer
    if (copied) {
      timer = setTimeout(() => {
        setCopied(false)
      }, 3000)
    }
    return () => clearTimeout(timer)
  }, [copied])

  const editScenario = async () => {
    if (currentMatch?.isGroup) return
    if (!user) {
      router.push('/signup')
      return
    }
    if (isEditScenarioLoading) {
      return
    }
    if (scenario === newScenario || newScenario === '') {
      return
    }
    setIsEditScenarioLoading(true)

    try {
      const idToken = await getIdToken(user)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/edit_scenario`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            id: currentMatch?.id,
            new_scenario: newScenario,
          }),
        },
      )

      const data = await response.json()

      if (response.ok) {
        // Update the matches state to reflect the change in story_mode for the updated match
        setMatches((prevMatches) =>
          prevMatches?.map((match) => {
            if (match.id === currentMatch?.id) {
              return { ...match, scenario: data.new_scenario }
            }
            return match
          }),
        )
      } else {
        if (response.status === 300) {
          // Same as editPersonality: don't discard what the user wrote.
          setErrorText(
            <div className="text-center">
              {data?.banned_word
                ? `Banned word detected: ${data.banned_word}.`
                : 'Banned word detected.'}
            </div>,
          )
          setIsErrorModalOpen(true)
        } else {
          if (isPlusOrHigher) {
            setErrorText(
              <div className="text-center">
                Something went wrong. Please try again.
              </div>,
            )
            setIsErrorModalOpen(true)
          } else {
            setLimitTitle(`🚀 ${t('upgradetogetpriorityaccess')}`)

            setErrorText(
              <div className="text-center">
                {t('freeserversarecurrentlyoverloaded')}{' '}
                <a href="/upgrade" className="underline text-white">
                  {t('upgradeyouraccount')}
                </a>{' '}
                {t('toenjoyfasterspeedsandpriorityaccess')}{' '}
              </div>,
            )
            setIsLimitModalOpen(true)
          }
        }
        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
    } finally {
      setIsToggleRoleplayLoading(false)
      setIsEditScenarioLoading(false)
    }
  }

  // Edit the character's personality. Unlike the scenario (which sets up the
  // opening scene and therefore prompts to clear the chat), personality is an
  // ongoing trait — the change simply takes effect on the next reply, so we do
  // NOT wipe chat history. Mirrors editScenario: POSTs to the backend, which
  // mutates only the user's private copy of the character (user_profiles).
  const editPersonality = async () => {
    if (currentMatch?.isGroup) return
    if (!user) {
      router.push('/signup')
      return
    }
    if (isEditPersonalityLoading) {
      return
    }
    if (personality === newPersonality || newPersonality === '') {
      return
    }
    setIsEditPersonalityLoading(true)

    try {
      const idToken = await getIdToken(user)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/edit_personality`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            id: currentMatch?.id,
            new_personality: newPersonality,
          }),
        },
      )

      const data = await response.json()

      if (response.ok) {
        // Optimistically patch the local copy of the match so the new
        // personality is reflected immediately.
        setMatches((prevMatches) =>
          prevMatches?.map((match) => {
            if (match.id === currentMatch?.id) {
              return { ...match, personality: data.new_personality }
            }
            return match
          }),
        )
        setIsEditingPersonality(false)
      } else {
        if (response.status === 300) {
          // Keep the draft. Clearing it here threw away everything the user had
          // typed, so a single banned word read as "editing wiped my
          // personality" — they can now fix the word and save again.
          setErrorText(
            <div className="text-center">
              {data?.banned_word
                ? `Banned word detected: ${data.banned_word}.`
                : 'Banned word detected.'}
            </div>,
          )
          setIsErrorModalOpen(true)
        } else {
          setErrorText(
            <div className="text-center">
              Something went wrong. Please try again.
            </div>,
          )
          setIsErrorModalOpen(true)
        }
        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
    } finally {
      setIsEditPersonalityLoading(false)
    }
  }

  // Take/upload a photo for the character to react to. Downscale + compress to a
  // base64 JPEG data URL (same canvas approach as LoraModal's handleCustomPose)
  // and stage it for the next send.
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return

    if (!user) {
      setShowSignUpGate(true)
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target.result
      img.onload = () => {
        const MAX = 1024
        const scale = Math.min(1, MAX / Math.max(img.width, img.height))
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * scale)
        canvas.height = Math.round(img.height * scale)
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        setPendingPhoto(canvas.toDataURL('image/jpeg', 0.6))
      }
    }
    reader.readAsDataURL(file)
  }

  const sendMessage = async (
    rawText,
    sentDate = null,
    audio = null,
    redo = false,
  ) => {
    const nowIso = new Date().toISOString()

    const text = stripHTML(rawText || '')
      .replace(/\u00A0/g, ' ')
      .trim()

    // While a reply is loading nothing is sent (the `if (!isLoading)` below
    // has no else), so return before the composer and the staged photo are
    // cleared: the person's text stays to send once the reply lands. It used
    // to vanish (S19, 6 October 2026).
    if (isLoading) return

    setMessage('')

    // Consume the staged photo (never on a redo).
    const photo = redo ? null : pendingPhoto
    if (photo) setPendingPhoto(null)

    if (!(text?.trim() || audio || photo)) {
      return
    }

    // Guest has already used their one free exchange — re-open the blocking
    // sign-up gate instead of sending another message.
    if (!user && hasSentOfflineMessage) {
      setShowSignUpGate(true)
      return
    }

    // First charged message of the window on this model: say so, quietly, and send. Until
    // 2026-10-06 this was a blocking "ask first" modal (Upgrade / Switch / Continue with
    // Gems, 2026-09-17); the day it shipped, charged messages fell from ~8,600 to ~3,000 a
    // day — two in three people stopped rather than pay when asked. The product owner chose
    // a subtle notice instead: still told, never stopped. Once per model per window.
    if (
      chatTier(subscription?.plan) !== 'Pro' &&
      chatAllowanceRemaining?.[model?.code] === 0 &&
      !chatOutAcked.current[model?.code]
    ) {
      chatOutAcked.current[model?.code] = true
      showChatOutNotice(
        t('chatOutNotice', { model: model?.title, gems: model?.gems }),
      )
    }

    if (!isLoading) {
      const typingDelay = audio ? 0 : 1000 + Math.random() * 1000

      setIsLoading(true)
      setCurrentSentDate(sentDate)

      if (redo) {
        setMatches((prevMatches) =>
          prevMatches?.map((match) => {
            if (match.id !== currentMatch?.id) return match

            const chatHistory = [...(match.chat_history || [])]

            while (
              chatHistory.length > 0 &&
              chatHistory[chatHistory.length - 1].role === 'assistant'
            ) {
              chatHistory.pop()
            }

            return {
              ...match,
              chat_history: chatHistory,
            }
          }),
        )
      } else if (!sentDate && (text?.trim() || photo)) {
        const newMessage = {
          content: text || '',
          send_date: nowIso,
          role: 'user',
          format: audio ? 'audio' : 'text',
          // Local data URL so the photo shows immediately; the server persists
          // its own signed URL under the same key for reloads.
          ...(photo ? { message_photo_url: photo } : {}),
        }

        setMatches((prevMatches) =>
          prevMatches?.map((match) =>
            match.id === currentMatch?.id
              ? {
                  ...match,
                  chat_history: match.chat_history
                    ? [...match.chat_history, newMessage]
                    : [newMessage],
                }
              : match,
          ),
        )
      }

      setTimeout(async () => {
        setIsBotTyping(true)

        try {
          if (!user) {
            // Offline fallback: reply locally so the guest actually sees a
            // response, then flag that their free message is used up.
            const messagesToAdd = []

            if (!isRoleplay && selfiePhotosEnabled) {
              const selfie = selectOfflineSelfie(currentMatch?.id)
              if (selfie && !selfie.startsWith('path/to/')) {
                messagesToAdd.push({
                  content: selfie,
                  send_date: nowIso,
                  role: 'assistant',
                  format: 'selfie',
                })
              }
            }

            messagesToAdd.push({
              content: getRandomOfflineReply(currentMatch?.name || 'She'),
              send_date: nowIso,
              role: 'assistant',
            })

            setMatches((prevMatches) =>
              prevMatches?.map((match) =>
                match.id === currentMatch?.id
                  ? {
                      ...match,
                      chat_history: match.chat_history
                        ? [...match.chat_history, ...messagesToAdd]
                        : [...messagesToAdd],
                    }
                  : match,
              ),
            )

            setHasSentOfflineMessage(true)

            // Let the guest read the reply, then block further chat with a
            // sign-up gate (mirrors the /generate offline reveal).
            setTimeout(() => setShowSignUpGate(true), 1200)
            return
          }

          const idToken = await getIdToken(user)

          // Group chat: one call returns replies from up to 2 characters, each
          // tagged with its sender. The optimistic user message was already
          // appended above, so we only append the assistant replies here.
          if (currentMatch?.isGroup) {
            // Name a character while the round is generating (corrected per
            // reply as they're revealed), so it never reads "A & B are typing".
            setTypingName(
              currentMatch?.participant_names?.[0] || currentMatch?.name,
            )
            const groupRes = await fetch(
              `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/generate_group_chat`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: idToken,
                },
                body: JSON.stringify({
                  groupId: currentMatch.id,
                  message: text,
                  isRoleplay: isRoleplay,
                  language: i18n?.language,
                  version: model?.code,
                }),
              },
            )
            const groupData = await groupRes.json().catch(() => ({}))
            if (groupRes.ok) {
              const replies = Array.isArray(groupData?.messages)
                ? groupData.messages
                : []
              await revealGroupReplies(replies, currentMatch.id)
              // Group turns bill like 1:1 (one tier price per round), so the
              // balance and the allowance signal come back the same way.
              if (groupData?.gems != null) {
                setGems(groupData.gems)
              }
              syncAllowance(groupData)
            } else if (groupRes.status === 402) {
              showChatPaywall()
            } else {
              setErrorText(
                (groupData?.error ||
                  'Something went wrong. Please try again.') +
                  (groupData?.banned_word ? ` ${groupData.banned_word}` : ''),
              )
              setIsErrorModalOpen(true)
            }
            return
          }

          // Every model in MODELS is v2. The v1 route (/api/generate_chat) is
          // gone from the backend, so a model without `endpoint` posted to a
          // 404 (S11, 6 October 2026).
          const response = await fetch(
            `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/generate_chat_v2`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: idToken,
              },
              body: JSON.stringify({
                message: text,
                characterId: currentMatch.id,
                isRoleplay: isRoleplay,
                audioMessagesEnabled: isRoleplay ? false : audioMessagesEnabled,
                selfiePhotosEnabled: isRoleplay ? false : selfiePhotosEnabled,
                send_date: sentDate,
                userVoice: audio,
                // Raw base64 (no data: prefix) of the user's uploaded photo.
                message_photo: photo ? photo.split(',')[1] : undefined,
                language: i18n?.language,
                redo: redo,
                version: model?.code,
              }),
            },
          )

          const data = await response.json()

          if (response.ok) {
            let messagesToAdd = []

            if (data.audio) {
              setAudioReply(data.audio)
              messagesToAdd.push({
                content: data.audio,
                send_date: nowIso,
                role: 'assistant',
                format: 'audio',
              })
            } else {
              messagesToAdd.push({
                content: data.message,
                send_date: nowIso,
                role: 'assistant',
              })
            }

            if (data.selfie) {
              messagesToAdd.unshift({
                content: data.selfie,
                send_date: nowIso,
                role: 'assistant',
                format: 'selfie',
              })
              setSelfieReply(data.selfie)
            }

            setMatches((prevMatches) =>
              prevMatches?.map((match) =>
                match.id === currentMatch?.id
                  ? {
                      ...match,
                      chat_history: match.chat_history
                        ? [...match.chat_history, ...messagesToAdd]
                        : [...messagesToAdd],
                    }
                  : match,
              ),
            )

            if (Number.isFinite(data.gems)) {
              setGems(data.gems)
            }
            syncAllowance(data)
          } else if (response.status === 402) {
            // Out of gems past the free allowance: the paywall, not an error.
            showChatPaywall()
          } else {
            // Was `error + ' ' + banned_word`, which rendered "undefined" on
            // every error that wasn't a banned word.
            setErrorText(
              [data?.error, data?.banned_word].filter(Boolean).join(' ') ||
                t('somethingWentWrong'),
            )
            setIsErrorModalOpen(true)
          }
        } catch (error) {
          console.error('Failed to send message:', error)
          setErrorText('Something went wrong. Please try again!')
          setIsErrorModalOpen(true)
        } finally {
          setIsBotTyping(false)
          setIsLoading(false)
          setCurrentSentDate(null)
          setTypingName(null)
        }
      }, typingDelay)
    }
  }

  // The out-of-gems paywall (a 402). Free / Plus / Premium are pitched Pro —
  // the tier whose chat is unlimited — with a link to the daily Gems; Pro /
  // Studio / Max are offered Gems (the modal's confirm already routes them).
  const showChatPaywall = () => {
    const plan = subscription?.plan
    const isTopPlan = chatTier(plan) === 'Pro'
    const n = chatAllowance(model, plan)
    const hasNumbers = Boolean(n && model?.title)
    const vars = { n, model: model?.title, gems: model?.gems }

    setLimitTitle(
      isTopPlan ? `💎 ${t('outOfGems')}` : `👑 ${t('upgradeToPro')}`,
    )
    setLimitConfirmation(isTopPlan ? t('getGems') : t('upgradeToPro'))
    setErrorText(
      <div className="text-center">
        {hasNumbers ? t('chatPaywallOutOfGems', vars) : t('youreOutOfGems')}
        <br />
        <br />
        {isTopPlan
          ? hasNumbers
            ? t('chatPaywallTopUp', vars)
            : t('chatPaywallTopUpGeneric')
          : t('chatPaywallProPitch', vars)}
        {!isTopPlan && (
          <>
            <br />
            <br />
            <Link href="/earn-gems" className="underline text-purple-300">
              {t('chatPaywallEarnGems')}
            </Link>
          </>
        )}
      </div>,
    )
    setIsLimitModalOpen(true)
  }

  // Every reply says how many free messages are left on the model it was sent
  // to. Kept per model code; a positive number re-arms the notice (the window
  // reset), so it is shown once per model per window.
  const syncAllowance = (data) => {
    const r = data?.allowance_remaining
    const code = model?.code
    if (r === undefined || r === null || !code) return
    setChatAllowanceRemaining((prev) => ({ ...(prev || {}), [code]: r }))
    if (r > 0) delete chatOutAcked.current[code]
  }

  const generateNarration = async (message) => {
    if (!user) {
      router.push('/signup')
      return
    }
    if (isNarrationLoading) {
      return
    }
    if (gems <= 0) {
      setErrorText(
        <div className="text-center">
          {t('outOfGems')}
          <br />
          <Link href="/upgrade" className="underline text-purple-300">
            {t('upgradeyouraccount')}
          </Link>{' '}
          {t('nowtounlockunlimitedimages')}
        </div>,
      )

      setIsLimitModalOpen(true)
      setLimitTitle(`🔥 ${t('upgradetocontinuegenerating')}`)
      return
    }
    setIsNarrationLoading(true)
    setCurrentLoadingNarration(message)

    try {
      const idToken = await getIdToken(user)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/generate_narration`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            characterId: currentMatch?.id,
            message: message,
          }),
        },
      )

      const data = await response.json()

      if (response.ok) {
        // Update the matches state to reflect the change in story_mode for the updated match
        if (data.audio) {
          setMatches((prevMatches) =>
            prevMatches?.map((match) =>
              match.id === currentMatch?.id
                ? {
                    ...match,
                    chat_history: match.chat_history.map((chat) =>
                      chat.content === message
                        ? { ...chat, narration_clip: data.audio }
                        : chat,
                    ),
                  }
                : match,
            ),
          )
        }

        if (Number.isFinite(data.gems)) {
          setGems(data.gems)
        }
      } else {
        if (
          subscription &&
          (subscription?.plan === 'Plus' ||
            subscription?.plan === 'Premium' ||
            subscription?.plan === 'Pro' ||
            subscription?.plan === 'Studio')
        ) {
          setErrorText(
            <div className="text-center">
              Servers are currently overloaded. Please try again.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else {
          setLimitTitle(`🚀 ${t('upgradetogetpriorityaccess')}`)

          setErrorText(
            <div className="text-center">
              {t('freeserversarecurrentlyoverloaded')}{' '}
              <a href="/upgrade" className="underline text-white">
                {t('upgradeyouraccount')}
              </a>{' '}
              {t('toenjoyfasterspeedsandpriorityaccess')}{' '}
            </div>,
          )
          setIsLimitModalOpen(true)
        }

        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
    } finally {
      setIsNarrationLoading(false)
    }
  }

  // Persist the two chat-video preferences alongside the other chat prefs.
  useEffect(() => {
    safeSetItem('chatVideoLength', String(chatVideoLength))
  }, [chatVideoLength])

  // Total cost of a scene video: the base frame (same image the "create image" button makes)
  // plus the clip. Shown on the button because the clip price moves with the panel settings,
  // and a button whose cost silently tripled because of a setting changed days ago is the
  // "gems taken by surprise" complaint in a new place.
  const chatVideoGemCost =
    chatImageGemCost(currentMatch?.isGroup, aiCharacterVersion) +
    ltxGemsFor(chatVideoLength, CHAT_VIDEO_FPS, CHAT_VIDEO_RESOLUTION)

  const generateSceneVideo = async () => {
    if (isVideoLoading || isImageLoading) return
    if (!user) return
    if (gems < chatVideoGemCost) {
      setErrorText(
        <div className="text-center">
          {t('outOfGems')}
          <br />
          <Link href="/upgrade" className="underline text-purple-300">
            {t('upgradeyouraccount')}
          </Link>
        </div>,
      )
      setIsLimitModalOpen(true)
      return
    }

    setIsVideoLoading(true)
    setVideoStatusText('Setting up…')

    try {
      const idToken = await getIdToken(user)

      // 1. Chat backend: pick the spoken line and render the character-accurate frame.
      //    Returns 422 with no_dialogue when the last messages are pure narration — there is
      //    nothing to speak, and inventing a line would put words in the character's mouth.
      const sceneResponse = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/generate_video_scene`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            characterId: currentMatch?.id,
            videoLength: chatVideoLength,
            // Use the same character path as this user's chat images, so the clip's
            // identity pipeline (and its price) matches what they already get.
            aiCharacterVersion,
            // Chat mode films a selfie; roleplay films the scene.
            isRoleplay,
          }),
        },
      )
      const sceneJson = await sceneResponse.json()

      if (!sceneResponse.ok) {
        console.error('Chat video scene failed:', sceneJson)
        setErrorText(
          <div className="text-center">
            {sceneJson?.no_dialogue
              ? 'There is no spoken line in this part of the scene yet — reply once more, then try again.'
              : sceneJson?.error || 'Could not set up the video.'}
            {sceneJson?.exception && (
              <div className="mt-2 text-xs opacity-70 break-words">
                {String(sceneJson.exception).slice(0, 300)}
              </div>
            )}
          </div>,
        )
        setIsErrorModalOpen(true)
        return
      }

      // 2. Video backend (a different host) — the SAME submit the Create page uses, so gems,
      //    moderation, queueing and delivery all stay on the one path that already works.
      setVideoStatusText('Filming…')
      const videoHost = process.env.NEXT_PUBLIC_PROMPTCHAN_VIDEO
      const submitResponse = await fetch(`${videoHost}/api/video_v3.1/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: idToken,
        },
        body: JSON.stringify({
          video_version: 'video5',
          image_base64: sceneJson.image,
          prompt: sceneJson.prompt,
          aspect: CHAT_VIDEO_ASPECT,
          seed: 0,
          userPlan: subscription?.plan || 'Free',
          folderSetting: 'Chat Images',
          visibility_mode: 'Offline',
          audioEnabled: true,
          upscale_video: false,
          loop: false,
          video_quality: 'Standard',
          frame_rate: CHAT_VIDEO_FPS,
          dialogue: sceneJson.dialogue,
          dialogue_speaker: sceneJson.dialogue_speaker,
          video_length: chatVideoLength,
          video_fps: CHAT_VIDEO_FPS,
          video_resolution: CHAT_VIDEO_RESOLUTION,
        }),
      })
      const submitJson = await submitResponse.json()

      if (!submitResponse.ok || !submitJson?.request_id) {
        // The submit's 500 carries the real cause in `exception` while `error` is the
        // generic "An internal server error occurred" — surface both, or every failure
        // looks identical and is undiagnosable from a user report.
        console.error('Chat video submit failed:', submitJson)
        setErrorText(
          <div className="text-center">
            {submitJson?.error || 'Could not start the video.'}
            {submitJson?.exception && (
              <div className="mt-2 text-xs opacity-70 break-words">
                {String(submitJson.exception).slice(0, 300)}
              </div>
            )}
          </div>,
        )
        setIsErrorModalOpen(true)
        return
      }

      // 3. Poll. A clip is ~45s at Fast, so this is minutes-tolerant rather than seconds.
      const requestId = submitJson.request_id
      // 12 minutes: a clip whose pod restarts is re-sent to another pod and still lands, minutes later.
      const deadline = Date.now() + 12 * 60 * 1000
      let videoUrl = null
      let videoDocRef = null
      let failed = false
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000))
        // A fresh token each round (cached, renewed near expiry), so a long wait doesn't end on "Token expired".
        const pollToken = await getIdToken(user).catch(() => idToken)
        const statusResponse = await fetch(
          `${videoHost}/api/video_v3.1/status/${requestId}`,
          { headers: { Authorization: pollToken } },
        )
        const statusJson = await statusResponse.json().catch(() => ({}))
        // Compared case-INSENSITIVELY on purpose. check_status_logic returns
        // "Completed" / "InProgress" / "Failed" (capitalised), and this used to test
        // for lowercase 'completed', which never matched — so chat video could not
        // succeed at all: every clip polled the full 6 minutes and then showed the
        // "taking longer than expected" message, even though the render had finished
        // in under a minute. The failure branch had the same mismatch, so failures
        // never short-circuited either. Create.jsx compares against 'Completed'
        // exactly; normalising here means a casing change on either side can't
        // silently break this a second time.
        const statusValue = String(statusJson?.status ?? '').toLowerCase()
        if (statusValue === 'completed' || statusValue === 'success') {
          const resultResponse = await fetch(
            `${videoHost}/api/video_v3.1/result/${requestId}`,
            { headers: { Authorization: pollToken } },
          )
          const resultJson = await resultResponse.json()
          if (String(resultJson?.status ?? '').toLowerCase() === 'success') {
            videoUrl = (resultJson?.video || [])[0] || null
            // publish_video identifies the clip by its DOC id, not by bytes the way
            // publish_image does — so the ref has to be carried on the message, or the
            // clip can never be published afterwards.
            videoDocRef = (resultJson?.doc_refs || [])[0] || null
            if (resultJson?.gems !== undefined) setGems(resultJson.gems)
          }
          break
        }
        // A definite failure stops here; an error reply (a blip, a token) just waits for the next round.
        if (statusValue === 'failed') {
          failed = true
          break
        }
      }

      if (failed) {
        setErrorText(
          <div className="text-center">
            The video could not be made. Your Gems have been refunded.
          </div>,
        )
        setIsErrorModalOpen(true)
        return
      }

      if (!videoUrl) {
        // Gems are auto-refunded by the backend safety window if the job never lands.
        setErrorText(
          <div className="text-center">
            Your video is taking longer than expected — it keeps running in the
            background. If it cannot finish, your Gems are refunded
            automatically.
          </div>,
        )
        setIsErrorModalOpen(true)
        return
      }

      // Deliver into the thread, exactly as a scene image does — the clip is part of the
      // conversation, not a modal that interrupts it.
      setMatches((prevMatches) =>
        prevMatches?.map((match) =>
          match.id === currentMatch?.id
            ? {
                ...match,
                chat_history: [
                  ...match.chat_history,
                  {
                    role: 'assistant',
                    content: '',
                    message_video: videoUrl,
                    message_video_ref: videoDocRef,
                    send_date: new Date().toISOString(),
                  },
                ],
              }
            : match,
        ),
      )
    } catch (error) {
      console.error('Chat video failed:', error)
      setErrorText(
        <div className="text-center">Could not create the video.</div>,
      )
      setIsErrorModalOpen(true)
    } finally {
      setIsVideoLoading(false)
      setVideoStatusText('')
    }
  }

  const generateImage = async () => {
    if (!user) {
      router.push('/signup')
      return
    }
    if (isImageLoading) {
      return
    }
    if (!isStudioOrHigher) {
      setLimitTitle(`🚀 ${t('upgradetogetpriorityaccess')}`)

      setErrorText(
        <div className="text-center">
          {t('youveRunOut')}{' '}
          <a href="/upgrade" className="underline text-white">
            {t('upgradeyouraccount')}
          </a>
        </div>,
      )
      setIsLimitModalOpen(true)
      return
    } else if (gems <= 0) {
      setErrorText(
        <div className="text-center">
          {t('outOfGems')}
          <br />
          <Link href="/upgrade" className="underline text-purple-300">
            {t('upgradeyouraccount')}
          </Link>{' '}
          {t('nowtounlockunlimitedimages')}
        </div>,
      )

      setIsLimitModalOpen(true)
      setLimitTitle(`🔥 ${t('upgradetocontinuegenerating')}`)
      return
    }
    setIsImageLoading(true)

    try {
      const idToken = await getIdToken(user)

      const endpoint =
        aiCharacterVersion === 'v3'
          ? 'generate_image_roleplay_v3'
          : 'generate_image_roleplay_v4'
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            characterId: currentMatch?.id,
          }),
        },
      )

      const data = await response.json()

      if (response.ok) {
        // Update the matches state to reflect the inclusion of the generated image in the chat history
        if (data.image) {
          setMatches((prevMatches) =>
            prevMatches?.map((match) =>
              match.id === currentMatch?.id
                ? {
                    ...match,
                    chat_history: [
                      ...match.chat_history,
                      {
                        role: 'assistant',
                        content: '', // or add caption text if you want
                        message_photo: data.image,
                        send_date: new Date().toISOString(),
                      },
                    ],
                  }
                : match,
            ),
          )
        }

        if (Number.isFinite(data.gems)) {
          setGems(data.gems)
        }
      } else {
        // These routes answer 479 for two different things, told apart by `error`:
        //   "No face error"                — the character's picture has no detectable face;
        //   "Chat image generation failed" — generate() came back without an image (chat2_bp.py),
        //     having either charged nothing (e.g. not enough gems) or refunded the charge.
        // Every 479 used to say "no face". 448 (GPU failure; Flame's is 502/504) and 500 come
        // after generate()'s own refund. (2026-10-02)
        if (response.status === 479 && data?.error === 'No face error') {
          setErrorText(
            <div className="text-center">
              Sorry, the AI could not detect a face in the image. Please try a
              different image with a clear face.
            </div>,
          )
          setIsErrorModalOpen(true)
          setCharImage(null)
        } else if ([448, 502, 504].includes(response.status)) {
          setErrorText(
            <div className="text-center">{t('imageFailedTemporary')}</div>,
          )
          setIsErrorModalOpen(true)
        } else if ([479, 500].includes(response.status)) {
          setErrorText(
            <div className="text-center">{t('imageFailedRefunded')}</div>,
          )
          setIsErrorModalOpen(true)
        } else if (
          subscription &&
          (subscription?.plan === 'Plus' ||
            subscription?.plan === 'Premium' ||
            subscription?.plan === 'Pro' ||
            subscription?.plan === 'Studio')
        ) {
          setErrorText(
            <div className="text-center">
              Servers are currently overloaded. Please try again.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else {
          setLimitTitle(`🚀 ${t('upgradetogetpriorityaccess')}`)

          setErrorText(
            <div className="text-center">
              {t('freeserversarecurrentlyoverloaded')}{' '}
              <a href="/upgrade" className="underline text-white">
                {t('upgradeyouraccount')}
              </a>{' '}
              {t('toenjoyfasterspeedsandpriorityaccess')}{' '}
            </div>,
          )
          setIsLimitModalOpen(true)
        }

        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
      // A request that never got a readable answer (network drop, Cloudflare's 100 s
      // cut-off, whose page has no CORS header) used to end with the spinner stopping and
      // nothing said at all.
      if (error?.message !== 'Response not ok') {
        setErrorText(
          <div className="text-center">{t('connectionLostTryAgain')}</div>,
        )
        setIsErrorModalOpen(true)
      }
    } finally {
      setIsImageLoading(false)
    }
  }

  // Group "create image from scene": always AI Character v4/Qwen — the backend
  // puts both characters into one scene. Same Studio + gems gating as the 1:1
  // version, and the returned image is appended the same way.
  const generateGroupImage = async () => {
    if (!user) {
      router.push('/signup')
      return
    }
    if (isImageLoading) return
    if (!isStudioOrHigher) {
      setLimitTitle(`🚀 ${t('upgradetogetpriorityaccess')}`)
      setErrorText(
        <div className="text-center">
          {t('youveRunOut')}{' '}
          <a href="/upgrade" className="underline text-white">
            {t('upgradeyouraccount')}
          </a>
        </div>,
      )
      setIsLimitModalOpen(true)
      return
    } else if (gems <= 0) {
      setErrorText(
        <div className="text-center">
          {t('outOfGems')}
          <br />
          <Link href="/upgrade" className="underline text-purple-300">
            {t('upgradeyouraccount')}
          </Link>{' '}
          {t('nowtounlockunlimitedimages')}
        </div>,
      )
      setIsLimitModalOpen(true)
      setLimitTitle(`🔥 ${t('upgradetocontinuegenerating')}`)
      return
    }

    setIsImageLoading(true)
    try {
      const idToken = await getIdToken(user)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/generate_group_image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            groupId: currentMatch?.id,
          }),
        },
      )
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.image) {
        setMatches((prevMatches) =>
          prevMatches?.map((match) =>
            match.id === currentMatch?.id
              ? {
                  ...match,
                  chat_history: [
                    ...(match.chat_history || []),
                    {
                      role: 'assistant',
                      content: '',
                      message_photo: data.image,
                      send_date: new Date().toISOString(),
                    },
                  ],
                }
              : match,
          ),
        )
        if (Number.isFinite(data.gems)) setGems(data.gems)
      } else {
        // 479 is "Group image generation failed" (generate() came back without an image,
        // having charged nothing or refunded). group_bp.py registers no APIError handler, so
        // every refusal or failure raised inside generate() — a GPU failure's 448 included —
        // arrives as a bare 500 "Internal server error", raised either before the charge or
        // after generate()'s own refund. Showing that string verbatim told the user nothing.
        // (2026-10-02)
        const failedInRender = [479, 500].includes(response.status)
        setErrorText(
          <div className="text-center">
            {failedInRender
              ? t('imageFailedRefunded')
              : data?.error ||
                'Sorry, that scene image failed. Please try again.'}
          </div>,
        )
        setIsErrorModalOpen(true)
      }
    } catch (error) {
      console.error('Group image generation failed:', error)
      setErrorText(
        <div className="text-center">{t('connectionLostTryAgain')}</div>,
      )
      setIsErrorModalOpen(true)
    } finally {
      setIsImageLoading(false)
    }
  }

  const publishContent = async (msg, index) => {
    try {
      if (!user) {
        setSignUpModalOpen(true)
        return
      }

      // Public → public profile gallery; Private → visible only to the user.
      const visibility = publishVisibility === 'Public' ? 'Public' : 'Private'
      const folder = null

      // Two shapes, mirroring /generate: publish_image takes the image BYTES (chat images
      // live on the message as base64 message_photo), publish_video takes the video's DOC
      // id — the clip itself is already stored server-side.
      const isVideo = !!msg?.message_video
      const endpoint = isVideo ? 'publish_video' : 'publish_image'
      const image = isVideo ? msg?.message_video_ref : msg?.message_photo

      if (!image) {
        setErrorText(
          isVideo
            ? 'This clip cannot be published — it was created before publishing was available.'
            : 'No image found to publish.',
        )
        setIsErrorModalOpen(true)
        return
      }

      const publishData = {
        image,
        folder,
        visibility,
      }

      setIsPublishing(true)

      const idToken = await getIdToken(user)

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify(publishData),
        },
      )

      const jsonResponse = await response.json()

      if (response.ok) {
        setPublishModalOpen(false)
      } else if (response.status === 403) {
        setErrorText(
          <div className="text-center">
            You must be a Plus or higher to publish.
          </div>,
        )
        setLimitTitle('Plus Feature')
        setIsLimitModalOpen(true)
      } else {
        setErrorText(jsonResponse?.error || 'Failed to publish.')
        setIsErrorModalOpen(true)
      }
    } catch (error) {
      console.error('Failed to publish chat image:', error)
      setErrorText('Failed to publish.')
      setIsErrorModalOpen(true)
    } finally {
      setIsPublishing(false)
      setPublishModalOpen(false)
    }
  }

  const useAnimate = async (msg, index) => {
    try {
      if (!user) {
        setSignUpModalOpen(true)
        return
      }

      const modelMap = {
        Real: 'Real',
        Hyperreal: 'Hyperreal',
        Anime: 'Anime',
        Hyperanime: 'Anime',
        'K-Pop': 'Real',
        Fur: 'Hyperreal',
      }

      const mappedModel = modelMap.hasOwnProperty(createModel)
        ? modelMap[createModel]
        : createModel

      const imageData = `${msg.message_photo}`
      const refId = msg?.id || msg?.send_date || `${currentMatch?.id}-${index}`

      setVideoRefImage(imageData)
      setVideoRefId(refId)
      setVideoRefLora(lora)
      setVideoPrompt(prompt || msg?.content || scenario || '')
      setVideoModel(mappedModel)
      setVideoImageSize(imageSize)
      setFormatTab('video')
      setVideoStartingPoint('image')
      setVideoId(refId)

      router.push('/generate')
    } catch (error) {
      console.error('Failed to animate chat image:', error)
    }
  }

  useEffect(() => {
    const match = matches?.find((m) => m?.id === currentMatch?.id)
    const chatHistory = match?.chat_history
    // Characters only carry story_mode once the user has flipped the toggle at
    // least once; until then it comes from their chat_mode_ab arm, which the
    // backend resolves identically (account.default_story_mode).
    const storyMode = match?.story_mode ?? defaultStoryMode
    const chatScenario = match?.scenario || ''
    const chatPersonality = match?.personality || ''

    if (!chatHistory || chatHistory.length === 0) {
      setCurrentChat([])
    } else {
      setCurrentChat(chatHistory)
    }

    setIsRoleplay(storyMode)

    setScenario(chatScenario)
    setPersonality(chatPersonality)
    // defaultStoryMode is a dep because it arrives with get_user_info, after the
    // first paint. Without it an arm-B user would sit in roleplay mode until
    // they happened to switch chats.
  }, [currentMatch, matches, defaultStoryMode])

  const sortMatches = (matches = []) => {
    return [...matches].sort((a, b) => {
      // Helper to get the most recent chat date (send_date or timestamp)
      const getLastChatDate = (match) => {
        const chat = match.chat_history || []
        if (chat.length === 0) return null
        const lastMessage = chat[chat.length - 1]
        return parseServerDate(
          lastMessage.send_date || lastMessage.timestamp || 0,
        )
      }

      const lastDateA = getLastChatDate(a)
      const lastDateB = getLastChatDate(b)

      // If both have chat history, sort by most recent chat (descending)
      if (lastDateA && lastDateB) return lastDateB - lastDateA

      // If only one has chat history, the one without should be first
      if (!lastDateA && lastDateB) return -1
      if (lastDateA && !lastDateB) return 1

      // If neither have chat history, sort by date_uploaded (descending)
      return (
        parseServerDate(b.date_uploaded || 0) -
        parseServerDate(a.date_uploaded || 0)
      )
    })
  }

  useEffect(() => {
    const sorted = sortMatches(matches)
    setSortedMatches(sorted)
  }, [matches])

  // No matches always means Discover. Having matches no longer forces a chat open:
  // fetchMatches decides (Discover by default, see there), and a tap on a chat or
  // the Messages list leaves Discover.
  useEffect(() => {
    if (matches?.length < 1 || !matches) setDiscover(true)
  }, [matches])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        matchesBoxRef.current &&
        !matchesBoxRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [matchesBoxRef])

  const handleSetVoice = async (voice) => {
    if (typeof window === 'undefined') return

    if (!user) {
      router.push('/signup')
      return
    }

    try {
      const idToken = await getIdToken(user)

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/set_voice`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            character_id: currentMatch?.id,
            voice: voice,
          }),
        },
      )

      const data = await response.json()

      if (response.ok) {
        // Update local state
        setCurrentMatch((prev) => ({
          ...prev,
          voice: voice,
        }))
      } else {
        console.error('Failed to update voice:', data.error)
      }
    } catch (error) {
      console.error('Something went wrong while setting voice:', error)
    }
  }

  if (matchesLoading) {
    return (
      <p className="text-white text-center my-12">
        <ClipLoader color="#FFF" size={24} />
      </p>
    )
  }

  return (
    <>
      {!user && showSignUpGate && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-sm py-8">
          <div className="w-full">
            <SignUpComponent message={t('signUpToChat')} />
          </div>
        </div>
      )}
      <div
        className={`bg-[var(--background-bg-color)]  overflow-hidden ${
          !user && showSignUpGate
            ? 'blur-[2px] pointer-events-none select-none'
            : ''
        }`}
      >
        <Modal
          isOpen={signUpModalOpen}
          closeModal={() => setSignUpModalOpen(false)}
          title={t('signUpToChatTitle')}
          content={t('signUpToChatContent')}
          confirmation={t('startChatting')}
          onConfirm={() => {
            router.push('/signup')
          }}
        />

        <ImageLightbox src={fullSrc} onClose={() => setFullSrc(null)} />

        <Modal
          isOpen={isErrorModalOpen}
          closeModal={() => setIsErrorModalOpen(false)}
          title={t('error')}
          content={errorText || t('somethingWentWrong')}
          confirmation={t('done')}
          onConfirm={() => setIsErrorModalOpen(false)}
        />

        <Modal
          isOpen={isLimitModalOpen}
          closeModal={closeLimitModal}
          title={limitTitle || `🚀 ${t('upgradetocontinuegenerating')}`}
          content={errorText || t('somethingWentWrong')}
          confirmation={limitConfirmation || t('upgradeandunlockbenefits')}
          onConfirm={() => {
            subscription?.plan === 'Pro' ||
            subscription?.plan === 'Studio' ||
            subscription?.plan === 'Max'
              ? router.push('/gems')
              : router.push('/upgrade')
          }}
        />

        <Modal
          isOpen={modalOpen}
          closeModal={() => setModalOpen(false)}
          title={
            currentMatch?.isGroup
              ? t('deleteGroupTitle', { defaultValue: 'Delete group?' })
              : t('unmatchCharacterTitle', { name: currentMatch?.name })
          }
          content={
            <>
              {currentMatch?.isGroup
                ? t('deleteGroupContent', {
                    defaultValue:
                      "This permanently deletes this group chat. Your characters aren't affected.",
                  })
                : t('unmatchCharacterContent')}
              {/* Unmatching recursive_deletes the character doc, which takes the
                  gifts subcollection with it. THIS is where a warning belongs —
                  real, paid-for value is destroyed and it cannot be undone. */}
              {currentShelf.length > 0 && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm text-amber-200">
                  <span className="pc-gift-shelf !ml-0 text-base">
                    {currentShelf.map((emoji, i) => (
                      <span key={i}>{emoji}</span>
                    ))}
                  </span>
                  <span>
                    {t('unmatchLosesGifts', {
                      defaultValue:
                        "The gifts you've given her will be gone for good.",
                    })}
                  </span>
                </div>
              )}
            </>
          }
          confirmation={
            currentMatch?.isGroup
              ? t('deleteGroup', { defaultValue: 'Delete group' })
              : t('yesUnmatch')
          }
          onConfirm={() => handleUnmatch(currentMatch?.id)}
          confirmation2={t('noGoBack')}
          onConfirm2={() => setModalOpen(false)}
        />

        <Modal
          isOpen={clearGiftsModalOpen}
          closeModal={() => setClearGiftsModalOpen(false)}
          title={t('clearGiftsTitle', {
            name: currentMatch?.name,
            defaultValue: 'Clear gifts?',
          })}
          content={
            <>
              {t('clearGiftsConfirm', {
                name: currentMatch?.name,
                defaultValue:
                  'This removes every gift and resets the relationship to how it started. Your gems are not refunded.',
              })}
              {currentShelf.length > 0 && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm text-amber-200">
                  <span className="pc-gift-shelf !ml-0 text-base">
                    {currentShelf.map((emoji, i) => (
                      <span key={i}>{emoji}</span>
                    ))}
                  </span>
                  <span>
                    {t('clearGiftsIrreversible', {
                      defaultValue: 'These will be gone for good.',
                    })}
                  </span>
                </div>
              )}
            </>
          }
          confirmation={t('clearGifts', { defaultValue: 'Clear gifts' })}
          onConfirm={handleClearGifts}
          confirmation2={t('noGoBack')}
          onConfirm2={() => setClearGiftsModalOpen(false)}
        />

        <Modal
          isOpen={clearModalOpen}
          closeModal={() => setClearModalOpen(false)}
          title={
            currentMatch?.isGroup
              ? t('clearGroupChatTitle', { defaultValue: 'Clear group chat?' })
              : t('clearCharacterChatTitle', { name: currentMatch?.name })
          }
          content={
            <>
              {currentMatch?.isGroup
                ? t('clearGroupChatContent', {
                    defaultValue: 'This clears every message in this group.',
                  })
                : t('clearCharacterChatContent')}
              {/* Clearing wipes chat_history and nothing else — the gift ledger
                  lives on the character doc and survives. So this REASSURES
                  rather than warns: a warning would imply a loss that does not
                  happen, and would discourage a free, safe action. */}
              {currentShelf.length > 0 && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-gray-300">
                  <span className="pc-gift-shelf !ml-0 text-base">
                    {currentShelf.map((emoji, i) => (
                      <span key={i}>{emoji}</span>
                    ))}
                  </span>
                  <span>
                    {t('clearKeepsGifts', {
                      name: currentMatch?.name,
                      defaultValue: `{{name}} keeps the gifts you've given her.`,
                    })}
                  </span>
                </div>
              )}
            </>
          }
          confirmation={t('clearConversation')}
          onConfirm={() => handleClear(currentMatch?.id)}
          confirmation2={t('noGoBack')}
          onConfirm2={() => setClearModalOpen(false)}
        />

        <Modal
          isOpen={roleplayModalOpen}
          closeModal={() => setRoleplayModalOpen(false)}
          title={t('clearYourChat')}
          content={t('clearChatModesContent')}
          confirmation={t('clearAndContinue')}
          onConfirm={async () => {
            await toggleRoleplay()
            await handleClear(currentMatch?.id)
          }}
          confirmation2={t('noGoBack')}
          onConfirm2={() => setRoleplayModalOpen(false)}
        />

        <Modal
          isOpen={scenarioModalOpen}
          closeModal={() => setScenarioModalOpen(false)}
          title={t('clearYourChat')}
          content={t('clearScenariosContent')}
          confirmation={t('clearAndContinue')}
          onConfirm={async () => {
            await editScenario()
            await handleClear(currentMatch?.id)
          }}
          confirmation2={t('noGoBack')}
          onConfirm2={() => setScenarioModalOpen(false)}
        />

        <Modal
          isOpen={createModalOpen}
          closeModal={() => setCreateModalOpen(false)}
          title={t('createYourCharacter')}
          content={<CreateCharacter safe={safeCountry} />}
          scrollable
          height
          width
        />

        <Modal
          isOpen={personaModalOpen}
          closeModal={() => setPersonaModalOpen(false)}
          title={t('myPersona', { defaultValue: 'My Persona' })}
          content={
            <MyPersona
              isOpen={personaModalOpen}
              closeModal={() => setPersonaModalOpen(false)}
            />
          }
          scrollable
        />

        <Modal
          isOpen={groupModalOpen}
          closeModal={() => setGroupModalOpen(false)}
          title={t('newGroup', { defaultValue: 'New Group' })}
          content={
            <CreateGroup
              characters={(matches || []).filter((m) => !m.isGroup)}
              onCreate={handleGroupCreated}
              closeModal={() => setGroupModalOpen(false)}
            />
          }
          scrollable
        />

        <Modal
          isOpen={photoSourceModalOpen}
          closeModal={() => setPhotoSourceModalOpen(false)}
          title={t('addAPhoto', { defaultValue: 'Add a photo' })}
          content={t('addAPhotoDesc', {
            defaultValue: 'Take a new photo or choose one from your gallery.',
          })}
          confirmation={t('takePhoto', { defaultValue: 'Take Photo' })}
          onConfirm={() => cameraInputRef.current?.click()}
          confirmation2={t('photoGallery', { defaultValue: 'Photo Gallery' })}
          onConfirm2={() => photoInputRef.current?.click()}
        />

        <Modal
          isOpen={upgradeModalOpen}
          closeModal={() => setUpgradeModalOpen(false)}
          title={t('upgradeRequiredTitle')}
          content={upgradeText}
          confirmation={t('upgrade')}
          onConfirm={() => router.push('/upgrade')}
        />

        <Modal
          isOpen={deleteAllProfilesModalOpen}
          closeModal={() => setDeleteAllProfilesModalOpen(false)}
          title={t('deleteAllData')}
          content={t('deleteAllDataContent')}
          confirmation={t('continue')}
          onConfirm={() => {
            setDeleteAllProfilesModalOpen(false)
            setConfirmDeleteAllModalOpen(true)
          }}
        />

        <Modal
          isOpen={confirmDeleteAllModalOpen}
          closeModal={() => setConfirmDeleteAllModalOpen(false)}
          title={t('thisCannotBeUndone')}
          content={t('deleteEverythingContent')}
          confirmation={t('deleteEverything')}
          onConfirm={handleDeleteAllProfiles}
        />

        <Modal
          isOpen={cardModalOpen}
          closeModal={() => setCardModalOpen(false)}
          title={currentMatch?.name || t('yourCharacter')}
          content={
            <>
              <div className="w-64">
                <ChatCard {...currentMatch} />
              </div>
            </>
          }
          width
        />

        <Modal
          isOpen={publishModalOpen}
          closeModal={() => {
            if (isPublishing) return
            setPublishModalOpen(false)
          }}
          title={t('publish')}
          content={
            <div>
              <p className="mb-3 text-base">Choose who can see this image.</p>

              <div className="mb-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={isPublishing}
                  onClick={() => setPublishVisibility('Private')}
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed ${
                    publishVisibility === 'Private'
                      ? 'border-[var(--foreground-bg-color)] bg-[var(--foreground-bg-color)] text-white'
                      : 'border-white/20 bg-[#0F0D14] text-white/80 hover:border-white/40'
                  }`}
                >
                  <FaLock className="text-xs" />
                  Private
                </button>

                <button
                  type="button"
                  disabled={isPublishing}
                  onClick={() => setPublishVisibility('Public')}
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed ${
                    publishVisibility === 'Public'
                      ? 'border-[var(--foreground-bg-color)] bg-[var(--foreground-bg-color)] text-white'
                      : 'border-white/20 bg-[#0F0D14] text-white/80 hover:border-white/40'
                  }`}
                >
                  <FaGlobeAmericas className="text-xs" />
                  Public
                </button>
              </div>

              <p className="mb-4 text-xs text-white/60">
                {publishVisibility === 'Public'
                  ? 'This image will appear on your public profile.'
                  : 'Only you will be able to see this image.'}
              </p>

              <div
                className={`mb-4 rounded-md p-2 transition ${
                  showTermsError ? 'ring-1 ring-red-500/70 bg-red-500/5' : ''
                }`}
              >
                <label className="flex items-start gap-2 text-xs text-white cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={isPublishing}
                    checked={acceptedPublishTerms}
                    onChange={(e) => {
                      const checked = e.target.checked
                      setAcceptedPublishTerms(checked)
                      if (checked) setShowTermsError(false)
                    }}
                    className="mt-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  />

                  <span className="leading-relaxed">
                    By publishing, you confirm that your content complies with
                    our{' '}
                    <a
                      href="/termsofservice"
                      className="underline underline-offset-2 hover:text-white"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Service
                    </a>{' '}
                    and does not violate our Prohibited Uses policy. Failure to
                    comply may result in a permanent account ban by our
                    moderation team.
                  </span>
                </label>

                {showTermsError && (
                  <p className="mt-1 text-xs text-red-400">
                    You must read and accept the terms.
                  </p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={isPublishing}
                  onClick={async () => {
                    if (isPublishing) return

                    if (!acceptedPublishTerms) {
                      setShowTermsError(true)
                      return
                    }

                    if (!publishTarget?.msg) {
                      setErrorText('No image selected to publish.')
                      setIsErrorModalOpen(true)
                      return
                    }

                    setShowTermsError(false)
                    await publishContent(publishTarget.msg, publishTarget.index)
                  }}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white
          bg-[var(--foreground-bg-color)] transition-transform duration-200 hover:scale-[1.05]
          disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 width-32"
                >
                  {isPublishing ? (
                    <>
                      <span
                        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
                        aria-hidden="true"
                      />
                      Publishing…
                    </>
                  ) : (
                    <>
                      {publishVisibility === 'Public' ? (
                        <FaGlobeAmericas />
                      ) : (
                        <FaLock />
                      )}
                      Publish
                    </>
                  )}
                </button>
              </div>
            </div>
          }
        />

        {chatArchiveModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 px-3 sm:px-6">
            <div className="w-full max-w-4xl h-[85vh] bg-[var(--background-bg-color)] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/10 bg-[var(--middle-bg-color)]">
                <div>
                  <h2 className="text-white text-lg sm:text-xl font-bold">
                    {t('fullChatArchive')}
                  </h2>
                  <p className="text-gray-400 text-xs sm:text-sm mt-1">
                    {currentMatch?.name || t('character')} ·{' '}
                    {Array.isArray(chatArchive) ? chatArchive.length : 0}{' '}
                    {t('messages')}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setChatArchiveModalOpen(false)}
                  className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
                >
                  <FaTimes size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4">
                {chatArchiveLoading ? (
                  <div className="h-full flex items-center justify-center">
                    <ClipLoader color="#FFF" size={28} />
                  </div>
                ) : !Array.isArray(chatArchive) || chatArchive.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center">
                    <FaComments className="text-gray-500 mb-3" size={30} />
                    <p className="text-white font-semibold">
                      {t('noArchivedChatFound')}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {t('noArchivedChatContent')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {chatArchive
                      .filter((msg) => !msg?.hidden)
                      .map((msg, index) => {
                        const isAssistant = msg?.role === 'assistant'
                        const date = msg?.send_date || msg?.timestamp
                        const content = msg?.content || msg?.message || ''

                        return (
                          <div
                            key={`${date || 'archive'}-${index}`}
                            className={`flex ${
                              isAssistant ? 'justify-start' : 'justify-end'
                            }`}
                          >
                            <div
                              className={`max-w-[85%] sm:max-w-[72%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                                isAssistant
                                  ? 'bg-[var(--middle-bg-color)] text-white rounded-bl-md'
                                  : 'bg-[var(--foreground-bg-color)] text-white rounded-br-md'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3 mb-1">
                                <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                                  {isAssistant
                                    ? currentMatch?.name || t('assistant')
                                    : t('you')}
                                </span>

                                {date && (
                                  <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                    {new Date(date).toLocaleString(undefined, {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                )}
                              </div>

                              {msg?.format === 'audio' ? (
                                <div className="text-gray-200 italic">
                                  {t('audioMessage')}
                                </div>
                              ) : msg?.format === 'selfie' || isUrl(content) ? (
                                <div className="text-gray-200 italic">
                                  {t('imageMessage')}
                                </div>
                              ) : msg?.message_photo ? (
                                <div className="text-gray-200 italic">
                                  {t('generatedImage')}
                                </div>
                              ) : (
                                <div>{content || t('emptyMessage')}</div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                  </div>
                )}
              </div>

              <div className="px-4 sm:px-6 py-3 border-t border-white/10 bg-[var(--middle-bg-color)] flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  {t('readOnlyArchiveView')}
                </p>

                <button
                  type="button"
                  onClick={() => setChatArchiveModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-[var(--foreground-bg-color)] text-white text-sm font-semibold"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {chatSettingsModalOpen && (
          <div
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/70 px-3 sm:px-6 py-8 overflow-y-auto"
            onClick={() => setChatSettingsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-2xl my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setChatSettingsModalOpen(false)}
                className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
              >
                <FaTimes size={16} />
              </button>
              <div className="bg-[var(--middle-bg-color)] rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
                <div className="text-white font-bold text-lg mr-auto">
                  {t('settings')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div className="px-2 sm:px-3 py-3">
                    <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                      <BsChatDotsFill
                        className="mr-1 text-white shrink-0"
                        size={12}
                      />
                      {t('chatModel')}
                    </span>
                    <div className="relative">
                      <button
                        onClick={() => {
                          setModelDropdownOpen(!modelDropdownOpen)
                        }}
                        className="w-full min-h-[40px] bg-[var(--foreground-bg-color)] px-3 py-2 rounded-full text-white flex relative font-semibold items-center justify-between gap-2 hover:opacity-90 transition-opacity"
                      >
                        <span className="text-xs truncate">
                          {MODELS.find((m) => m.code === model.code)?.title ||
                            t('selectModel')}
                        </span>
                        <FaChevronDown className="md:text-xs" />
                      </button>

                      {modelDropdownOpen && (
                        <div className="absolute mt-1.5 right-0 left-0 bg-[#1E1D1E] rounded-2xl border border-white/10 shadow-lg z-50 w-full max-h-72 overflow-y-auto p-2">
                          {(() => {
                            const visibleModels = MODELS.filter(
                              (m) =>
                                !(m.hide && (!user || displayPlan === 'Free')),
                            )

                            const activeModels = visibleModels.filter(
                              (m) => !m.legacy,
                            )
                            const legacyModels = visibleModels.filter(
                              (m) => m.legacy,
                            )

                            const renderModel = (m) => (
                              <div
                                key={m.code}
                                onClick={() => {
                                  if (m.plan === 'Plus' && !isPlusOrHigher) {
                                    setChatSettingsModalOpen(false)
                                    setUpgradeModalOpen(true)
                                    setUpgradeText(
                                      t('upgradeToAccessAllChatModels'),
                                    )
                                  } else {
                                    setModel(m)
                                    setModelDropdownOpen(false)
                                    setLegacyOpen(false)
                                  }
                                }}
                                className={`px-4 py-2 cursor-pointer hover:bg-[#333] rounded-xl ${
                                  m.code === model.code
                                    ? 'font-semibold text-white'
                                    : 'text-gray-300'
                                }`}
                              >
                                <p className="text-sm">{m.title}</p>

                                {m.description && (
                                  <p className="text-xxs text-gray-400 leading-snug mt-0.5 font-light">
                                    {m.description}
                                  </p>
                                )}

                                <span className="text-xs text-white inline-flex items-center gap-1 mt-1">
                                  {m.allowance &&
                                    (chatAllowance(m, subscription?.plan) ===
                                    null ? (
                                      <>
                                        <BsChatDotsFill size={10} />
                                        {t('chatUnlimited')}
                                        <span className="text-xxs text-gray-400">
                                          {t('chatFairUse')}
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <BsChatDotsFill size={10} />
                                        {chatAllowance(m, subscription?.plan)}
                                        <span className="text-xxs text-gray-400">
                                          {t('per3HoursThen')}
                                        </span>
                                        <FaGem size={10} /> {m.gems}
                                      </>
                                    ))}
                                </span>
                              </div>
                            )

                            return (
                              <>
                                {activeModels.map(renderModel)}

                                {legacyModels.length > 0 && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => setLegacyOpen(!legacyOpen)}
                                      className="w-full px-4 py-2 flex items-center justify-between text-left text-sm text-gray-300 hover:bg-[#333] rounded-xl"
                                    >
                                      <span>{t('legacyModels')}</span>
                                      <FaChevronDown
                                        className={`text-xs transition-transform ${
                                          legacyOpen ? 'rotate-180' : ''
                                        }`}
                                      />
                                    </button>

                                    {legacyOpen && (
                                      <div className="mt-1 space-y-1">
                                        {legacyModels.map(renderModel)}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                      {t('chatModelDescription', {
                        defaultValue:
                          'The AI model powering this chat — higher tiers reply better and remember more.',
                      })}
                    </p>
                  </div>
                  <div className="px-2 sm:px-3 py-3">
                    <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                      <FaTheaterMasks
                        className="mr-1 text-white shrink-0"
                        size={12}
                      />
                      {t('chatMode')}
                    </span>

                    <div className="flex items-center justify-center">
                      <div
                        className="relative inline-block w-full h-10 rounded-full bg-[var(--foreground-bg-color)] cursor-pointer select-none"
                        onClick={() => {
                          if (!user) {
                            setChatSettingsModalOpen(false)
                            setSignUpModalOpen(true)
                            return
                          }
                          if (currentChat?.length > 0) {
                            setChatSettingsModalOpen(false)
                            setRoleplayModalOpen(true)
                          } else {
                            toggleRoleplay()
                          }
                        }}
                      >
                        {/* Sliding Thumb */}
                        <div
                          className={`absolute z-20 left-0 top-0.5 flex items-center justify-center w-1/2 h-9 transition-all duration-300 transform rounded-full bg-white ${
                            isRoleplay ? 'translate-x-full' : 'translate-x-0'
                          }`}
                        >
                          {isRoleplay ? (
                            <div className="text-black flex items-center font-bold text-sm space-x-1">
                              <p>{t('story')}</p>
                              {isToggleRoleplayLoading ? (
                                <ClipLoader size={12} color="#FFFFFF" />
                              ) : null}
                            </div>
                          ) : (
                            <div className="text-black flex items-center font-bold text-sm space-x-1">
                              <p>{t('chat')}</p>
                              {isToggleRoleplayLoading ? (
                                <ClipLoader size={12} color="#FFFFFF" />
                              ) : null}
                            </div>
                          )}
                        </div>

                        {/* Labels */}
                        <span className="absolute left-0 top-0 flex items-center justify-center w-1/2 h-10 text-xs sm:text-sm font-medium text-white">
                          <p>{t('chat')}</p>
                        </span>
                        <span className="absolute right-0 top-0 flex items-center justify-center w-1/2 h-10 text-xs sm:text-sm font-medium text-white">
                          <p>{t('story')}</p>
                        </span>
                      </div>
                    </div>

                    <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                      {t('chatModeDescription')}
                    </p>
                  </div>

                  {!isRoleplay && (
                    <div className="px-2 sm:px-3 py-3">
                      <label className="flex items-center justify-between gap-3 cursor-pointer">
                        <span className="text-white text-sm font-semibold flex items-center">
                          <HiSpeakerWave
                            className="mr-1 text-white shrink-0"
                            size={12}
                          />
                          {t('voiceMessages')}
                        </span>
                        <input
                          type="checkbox"
                          checked={audioMessagesEnabled}
                          onChange={(e) =>
                            setAudioMessagesEnabled(e.target.checked)
                          }
                          data-track="chat.voice_messages_toggle"
                          className="h-4 w-4 cursor-pointer"
                        />
                      </label>
                      <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                        {t('voiceMessagesDescription')}
                      </p>
                    </div>
                  )}

                  {!currentMatch?.isGroup && (
                    <div className="px-2 sm:px-3 py-3">
                      <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <MdRecordVoiceOver
                          className="mr-1 text-white shrink-0"
                          size={12}
                        />
                        {t('voice')}
                      </span>

                      <div className="relative my-0.5 z-40">
                        <button
                          onClick={() => {
                            setVoiceModalOpen(!voiceModalOpen)
                            setAccentModalOpen(false)
                          }}
                          className="w-full sm:w-auto min-h-[40px] bg-[var(--foreground-bg-color)] px-3 py-2 rounded-full text-white flex relative font-semibold items-center justify-between sm:justify-center gap-2 hover:opacity-90 transition-opacity"
                        >
                          <span className="block sm:hidden text-xs truncate">
                            {VOICES.find((m) => m.code === currentMatch?.voice)
                              ?.title || t('selectVoice')}
                          </span>
                          <span className="hidden sm:block mr-2 text-xs">
                            {t('voice')}:{' '}
                            {VOICES.find((m) => m.code === currentMatch?.voice)
                              ?.title || t('select')}
                          </span>
                          <FaChevronDown className="text-xs shrink-0" />
                        </button>

                        {voiceModalOpen && (
                          <div className="absolute mt-1.5 right-0 left-0 sm:left-auto bg-[#1E1D1E] rounded-2xl border border-white/10 shadow-lg z-50 w-full sm:w-64 max-h-64 overflow-y-auto grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 p-1">
                            {VOICES.map((m) => (
                              <div
                                key={m.code}
                                onClick={() => {
                                  if (m.plan === 'Plus' && !isPlusOrHigher) {
                                    setChatSettingsModalOpen(false)
                                    setUpgradeModalOpen(true)
                                    setUpgradeText(
                                      t('upgradeToAccessMoreVoices'),
                                    )
                                  } else {
                                    handleSetVoice(m.code)
                                    setVoiceModalOpen(false)
                                  }
                                }}
                                className={`px-4 py-2.5 cursor-pointer hover:bg-[#333] rounded-xl flex items-center justify-center text-center ${
                                  m.code === currentMatch?.voice
                                    ? 'font-medium text-white bg-white/5'
                                    : 'text-gray-300'
                                }`}
                              >
                                <p className="text-sm">{m.title}</p>
                                {m.plan === 'Plus' &&
                                  subscription?.plan === 'Free' && (
                                    <FaLock className="ml-1 shrink-0" />
                                  )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <p className="mt-2 text-gray-400 text-xs leading-relaxed">
                        {t('voiceDescription')}
                      </p>
                    </div>
                  )}
                  {/* AI Character version: which model applies the character to
                      "Create image from scene" images (v4 = Qwen, v3 = IPAdapter FaceID).
                      Hidden for groups — group scene images are always v4/Qwen. */}
                  {!currentMatch?.isGroup && (
                    <div className="px-2 sm:px-3 py-3">
                      <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <TbFaceId
                          className="mr-1 text-white shrink-0"
                          size={14}
                        />
                        {t('aiCharacterVersion', {
                          defaultValue: 'AI Character Version',
                        })}
                      </span>

                      <div className="flex items-center justify-center">
                        <div
                          className="relative inline-block w-full h-10 rounded-full bg-[var(--foreground-bg-color)] cursor-pointer select-none"
                          onClick={() =>
                            setAiCharacterVersion((v) =>
                              v === 'v4' ? 'v3' : 'v4',
                            )
                          }
                        >
                          {/* Sliding thumb shows the active version */}
                          <div
                            className={`absolute z-20 left-0 top-0.5 flex items-center justify-center w-1/2 h-9 transition-all duration-300 transform rounded-full bg-white ${
                              aiCharacterVersion === 'v4'
                                ? 'translate-x-full'
                                : 'translate-x-0'
                            }`}
                          >
                            <p className="text-black font-bold text-sm">
                              {aiCharacterVersion === 'v4' ? 'v3' : 'v2'}
                            </p>
                          </div>

                          {/* Static labels (display v2/v3 to match /generate; the
                            underlying values stay v3/v4 → _v3/_v4 endpoints). */}
                          <span className="absolute left-0 top-0 flex items-center justify-center w-1/2 h-10 text-xs sm:text-sm font-medium text-white">
                            v2
                          </span>
                          <span className="absolute right-0 top-0 flex items-center justify-center w-1/2 h-10 text-xs sm:text-sm font-medium text-white">
                            v3
                          </span>
                        </div>
                      </div>

                      <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                        {t('aiCharacterVersionDescription', {
                          defaultValue:
                            'Which model applies your character to "Create image from scene" images. v3 is newest with stronger likeness; v2 is the previous version.',
                        })}
                      </p>
                    </div>
                  )}
                  <div className="px-2 sm:px-3 py-3">
                    <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                      <FaFilm className="mr-1 text-white shrink-0" size={12} />
                      {t('sceneVideo', { defaultValue: 'Scene video' })}
                    </span>
                    {/* Length is the only knob: it reads the shared LTX list, the same one
                      the Video tab and the backend use, so a second copy can never drift
                      into offering a length submit rejects. */}
                    <div className="flex gap-1">
                      {LTX_LENGTHS.map((l) => (
                        <button
                          key={l.value}
                          type="button"
                          onClick={() => setChatVideoLength(l.value)}
                          aria-pressed={chatVideoLength === l.value}
                          className={`flex-1 min-h-[36px] rounded-full px-3 text-xs font-semibold transition-opacity ${
                            chatVideoLength === l.value
                              ? 'bg-white text-black'
                              : 'bg-[var(--foreground-bg-color)] text-gray-300 hover:opacity-90'
                          }`}
                        >
                          {l.value}s
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2 flex items-center gap-1">
                      <FaGem size={8} />
                      {chatVideoGemCost}{' '}
                      {t('perVideo', { defaultValue: 'per video' })}
                    </p>
                  </div>
                </div>

                {!currentMatch?.isGroup && (
                  <div className="px-2 sm:px-3 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="block text-white text-sm font-semibold flex items-center">
                        <MdPsychology
                          className="mr-1 text-white shrink-0"
                          size={12}
                        />
                        {t('personality', { defaultValue: 'Personality' })}
                      </span>

                      {!isEditingPersonality ? (
                        <button
                          onClick={() => {
                            setNewPersonality(personality || '')
                            setIsEditingPersonality(true)
                          }}
                          className="px-3 py-1.5 rounded-xl bg-[var(--foreground-bg-color)] text-white text-xs font-semibold flex items-center gap-2"
                        >
                          <FaEdit size={12} />
                          {t('edit')}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setIsEditingPersonality(false)
                              setNewPersonality(personality || '')
                            }}
                            className="px-3 py-1.5 rounded-xl bg-[var(--foreground-bg-color)] text-white text-xs font-semibold"
                          >
                            {t('cancel')}
                          </button>

                          <button
                            onClick={editPersonality}
                            disabled={
                              isEditPersonalityLoading ||
                              !newPersonality.trim() ||
                              newPersonality.trim() === personality
                            }
                            className="px-3 py-1.5 rounded-xl bg-[var(--main-bg-color)] text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                          >
                            {isEditPersonalityLoading ? (
                              <ClipLoader size={12} color="#fff" />
                            ) : (
                              <FaSave size={12} />
                            )}
                            {t('save', { defaultValue: 'Save' })}
                          </button>
                        </div>
                      )}
                    </div>

                    {!isEditingPersonality ? (
                      <p className="italic text-sm font-light bg-[var(--foreground-bg-color)] p-3 rounded-xl tracking-tight text-white leading-relaxed break-words">
                        {personality?.trim()
                          ? `"${personality}"`
                          : t('noPersonalitySet', {
                              defaultValue: 'No personality set yet.',
                            })}
                      </p>
                    ) : (
                      <textarea
                        id="personality"
                        placeholder={t('enterYourPersonality', {
                          defaultValue: 'Enter personality',
                        })}
                        value={newPersonality}
                        maxLength={300}
                        onChange={(e) => setNewPersonality(e.target.value)}
                        className="rounded-xl px-3 py-2.5 w-full text-white bg-[var(--foreground-bg-color)] focus:outline-[var(--secondary-bg-color)] min-h-[96px] text-sm resize-none"
                        required
                      />
                    )}

                    <p className="mt-2 text-gray-400 text-xs leading-relaxed">
                      {t('personalityDescription', {
                        defaultValue:
                          'The character’s personality. This shapes how they act and respond in your chats.',
                      })}
                    </p>
                  </div>
                )}

                {currentMatch?.isGroup && (
                  <div className="px-2 sm:px-3 py-3">
                    <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                      <MdPsychology
                        className="mr-1 text-white shrink-0"
                        size={12}
                      />
                      {t('members', { defaultValue: 'Members' })}
                    </span>
                    {(currentMatch.participants || []).map((pid) => {
                      const char = matches?.find(
                        (m) => m.id === pid && !m.isGroup,
                      )
                      if (!char) return null
                      return (
                        <GroupMemberSettings
                          key={pid}
                          character={char}
                          user={user}
                          isPlusOrHigher={isPlusOrHigher}
                          voices={VOICES}
                          onUpdate={updateCharacterInMatches}
                          onUpgrade={() => {
                            setChatSettingsModalOpen(false)
                            setUpgradeModalOpen(true)
                            setUpgradeText(t('upgradeToAccessMoreVoices'))
                          }}
                        />
                      )
                    })}
                    <p className="mt-1 text-gray-400 text-xs leading-relaxed">
                      {t('groupMembersDescription', {
                        defaultValue:
                          'Each character keeps their own personality and voice — edits also apply to your 1:1 chat with them.',
                      })}
                    </p>
                  </div>
                )}

                {isRoleplay && (
                  <div className="px-2 sm:px-3 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="block text-white text-sm font-semibold flex items-center">
                        <MdMovie
                          className="mr-1 text-white shrink-0"
                          size={12}
                        />
                        {t('scenario')}
                      </span>

                      {!isEditingScenario ? (
                        <button
                          onClick={() => {
                            setNewScenario(scenario || '')
                            setIsEditingScenario(true)
                          }}
                          className="px-3 py-1.5 rounded-xl bg-[var(--foreground-bg-color)] text-white text-xs font-semibold flex items-center gap-2"
                        >
                          <FaEdit size={12} />
                          {t('edit')}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setIsEditingScenario(false)
                              setNewScenario(scenario || '')
                            }}
                            className="px-3 py-1.5 rounded-xl bg-[var(--foreground-bg-color)] text-white text-xs font-semibold"
                          >
                            {t('cancel')}
                          </button>

                          <button
                            onClick={() => {
                              if (currentChat?.length > 0) {
                                setChatSettingsModalOpen(false)
                                setScenarioModalOpen(true)
                              } else {
                                editScenario()
                              }
                            }}
                            disabled={
                              isEditScenarioLoading ||
                              !newScenario.trim() ||
                              newScenario.trim() === scenario
                            }
                            className="px-3 py-1.5 rounded-xl bg-[var(--main-bg-color)] text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                          >
                            {isEditScenarioLoading ? (
                              <ClipLoader size={12} color="#fff" />
                            ) : (
                              <FaSave size={12} />
                            )}
                            {t('save')}
                          </button>
                        </div>
                      )}
                    </div>

                    {!isEditingScenario ? (
                      <p className="italic text-sm font-light bg-[var(--foreground-bg-color)] p-3 rounded-xl tracking-tight text-white leading-relaxed break-words">
                        "{scenario}"
                      </p>
                    ) : (
                      <div>
                        <textarea
                          id="prompt"
                          placeholder={t('enterYourScenario')}
                          value={newScenario}
                          maxLength={300}
                          onChange={(e) => setNewScenario(e.target.value)}
                          className="rounded-xl px-3 py-2.5 w-full text-white bg-[var(--foreground-bg-color)] focus:outline-[var(--secondary-bg-color)] min-h-[96px] text-sm resize-none"
                          required
                        />
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-2">
                          <button
                            onClick={addSuggestionToPrompt}
                            className="flex-grow text-white bg-[var(--foreground-bg-color)] hover:bg-[var(--foreground-bg-color)]/90 px-4 py-2 rounded-xl text-xs min-h-[40px] text-left sm:text-center"
                          >
                            {truncate(currentSuggestion, 50)}
                          </button>
                          <button
                            onClick={refreshSuggestion}
                            className="h-10 w-full sm:w-10 rounded-xl bg-[var(--foreground-bg-color)] hover:bg-[var(--foreground-bg-color)]/90 flex items-center justify-center shrink-0"
                          >
                            <FaDice className="text-sm" />
                          </button>
                        </div>
                      </div>
                    )}

                    <p className="mt-2 text-gray-400 text-xs leading-relaxed">
                      {t('scenarioDescription')}
                    </p>
                  </div>
                )}

                {/* Gifts A/B. Only shown once something has actually been
                    gifted — an empty "clear gifts" row would just be clutter,
                    and for the control arm currentShelf is always empty. */}
                {currentShelf.length > 0 && (
                  <div className="px-2 sm:px-3 py-3">
                    <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                      <FaGift className="mr-1 shrink-0" size={12} />
                      {t('gifts', { defaultValue: 'Gifts' })}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="pc-gift-shelf !ml-0 text-lg">
                        {currentShelf.map((emoji, i) => (
                          <span key={i}>{emoji}</span>
                        ))}
                      </span>
                      <button
                        onClick={() => setClearGiftsModalOpen(true)}
                        disabled={clearingGifts}
                        className="ml-auto px-4 py-2.5 rounded-xl text-white bg-[var(--foreground-bg-color)] font-semibold min-h-[42px] disabled:opacity-50"
                      >
                        {clearingGifts
                          ? t('clearing', { defaultValue: 'Clearing…' })
                          : t('clearGifts', { defaultValue: 'Clear gifts' })}
                      </button>
                    </div>
                    <p className="mt-2 text-gray-400 text-xs leading-relaxed">
                      {t('clearGiftsDescription', {
                        defaultValue:
                          'Removes every gift and starts the relationship over. Gems are not refunded.',
                      })}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div className="px-2 sm:px-3 py-3">
                    <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                      <FaShare className="mr-1 shrink-0" size={12} />
                      {t('share')}
                    </span>

                    <button
                      onClick={shareChat}
                      className="px-4 py-2.5 rounded-xl w-full text-white bg-[var(--foreground-bg-color)] font-semibold min-h-[42px]"
                    >
                      {t('copy')}
                    </button>

                    <p className="mt-2 text-gray-400 text-xs leading-relaxed">
                      {t('shareChatDescription')}
                    </p>
                  </div>

                  <div className="px-2 sm:px-3 py-3">
                    <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                      <FaTrash className="mr-2 shrink-0" size={12} />
                      {t('clear')}
                    </span>

                    <button
                      onClick={() => {
                        if (!user) {
                          setChatSettingsModalOpen(false)
                          setSignUpModalOpen(true)
                          return
                        }
                        setChatSettingsModalOpen(false)
                        setClearModalOpen(true)
                      }}
                      className="px-4 py-2.5 rounded-xl w-full text-white bg-[var(--foreground-bg-color)] font-semibold min-h-[42px]"
                    >
                      {t('clear')}
                    </button>

                    <p className="mt-2 text-gray-400 text-xs leading-relaxed">
                      {t('clearChatDescription')}
                    </p>
                  </div>

                  {(currentMatch?.chat_history_archive_count || 0) > 0 && (
                    <div className="px-2 sm:px-3 py-3">
                      <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <FaArchive className="mr-2 shrink-0" size={12} />
                        {t('archive')}
                      </span>

                      <button
                        onClick={async () => {
                          if (!user) {
                            setChatSettingsModalOpen(false)
                            setSignUpModalOpen(true)
                            return
                          }

                          setChatSettingsModalOpen(false)
                          setChatArchiveModalOpen(true)
                          await fetchChatArchive(currentMatch?.id)
                        }}
                        className="px-4 py-2.5 rounded-xl w-full text-white bg-[var(--foreground-bg-color)] font-semibold min-h-[42px]"
                      >
                        {t('viewArchive')}
                      </button>

                      <p className="mt-2 text-gray-400 text-xs leading-relaxed">
                        {t('viewArchiveDescription')}
                      </p>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-white/5">
                  <div className="flex flex-wrap gap-2">
                    {/* Message */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-gray-300">
                      <span className="text-gray-400">{t('message')}</span>

                      {model?.allowance && (
                        <>
                          <BsChatDotsFill size={9} className="opacity-80" />
                          <span>
                            {chatAllowance(model, subscription?.plan) === null
                              ? t('chatUnlimited')
                              : chatAllowance(model, subscription?.plan)}
                          </span>
                          {chatAllowance(model, subscription?.plan) !==
                            null && <span className="text-gray-500">/3h</span>}
                        </>
                      )}

                      <span className="flex items-center gap-0.5 ml-1 text-white">
                        <FaGem size={9} />
                        {model?.gems || 0.5}
                      </span>
                    </div>

                    {/* Image (Create image from scene) — cost tracks the selected
                        AI Character version: batch 1 + character (v4 Qwen = 3, v3
                        face-swap = 1) + XL model surcharge (Ultra quality is free). */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-gray-300">
                      <span className="text-gray-400">{t('image')}</span>
                      <span className="text-gray-500">{t('from')}</span>
                      <span className="flex items-center gap-0.5 text-white">
                        <FaGem size={9} />{' '}
                        {1 +
                          (aiCharacterVersion === 'v4' ? 3 : 1) +
                          ((currentMatch?.model || '').includes('XL') ? 1 : 0)}
                      </span>
                    </div>

                    {/* Voice Call */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-gray-300">
                      <span className="text-gray-400">{t('call')}</span>
                      <span className="flex items-center gap-0.5 text-white">
                        <FaGem size={9} /> 2
                      </span>
                    </div>

                    {/* Voice Message */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-gray-300">
                      <span className="text-gray-400">{t('voiceMessage')}</span>
                      <span className="flex items-center gap-0.5 text-white">
                        <FaGem size={9} /> 1
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-row">
          {!callModalOpen && (
            <div
              ref={matchesBoxRef}
              className={`fixed z-20 inset-y-0 left-0 transform ${
                isOpen ? '' : '-translate-x-full'
              } md:w-1/3 w-2/3 transition-transform duration-200 ease-in-out
        bg-[var(--background-bg-color)] text-white shadow-lg p-4 px-2
        md:relative md:translate-x-0
        h-100vh md:h-[calc(100dvh-var(--pc-content-top,6rem))] overflow-y-auto`}
            >
              <div className="mb-4 py-4 md:pt-0 pt-32">
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="md:hidden"
                  >
                    <FaChevronLeft className="h-6 w-6 text-white m-2" />
                  </button>
                </div>

                {safe && !safeCountry && (
                  <Link
                    href="/settings"
                    className="mb-3 flex w-full items-center gap-2 rounded-2xl border border-[#7a74eb]/25 bg-[#6366f1]/10 px-3 py-2 text-xs hover:bg-[#6366f1]/20 transition"
                  >
                    <FaEyeSlash className="text-[#a5b4fc] shrink-0" size={12} />
                    <span className="text-gray-300 flex-1">
                      <span className="font-medium text-[#c7d2fe]">
                        {t('safeMode')}
                      </span>
                      : {t('nsfwHidden')}
                    </span>
                    <span className="shrink-0 text-[10px] font-semibold text-[#c7d2fe]">
                      {t('update')} ›
                    </span>
                  </Link>
                )}

                <div className="flex items-center justify-between pb-2 border-b border-gray-800 mb-4">
                  <h2 className="font-bold text-white">{t('messages')}</h2>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!user) {
                          setSignUpModalOpen(true)
                          return
                        }
                        setGroupModalOpen(true)
                        setIsOpen(false)
                      }}
                      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-[var(--foreground-bg-color)] text-white text-xs font-semibold hover:bg-[var(--main-bg-color)] hover:scale-[1.03] active:scale-95 transition-all duration-200 ${
                        groupModalOpen && 'opacity-50 pointer-events-none'
                      }`}
                    >
                      <TbUsersPlus size={16} className="shrink-0" />
                      <span className="whitespace-nowrap">
                        {t('newGroup', { defaultValue: 'New Group' })}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!user) {
                          setSignUpModalOpen(true)
                          return
                        }
                        setDeleteAllProfilesModalOpen(true)
                      }}
                      title={t('deleteAll')}
                      aria-label={t('deleteAll')}
                      className="h-9 w-9 flex items-center justify-center rounded-xl bg-[var(--foreground-bg-color)] text-gray-300 hover:bg-red-500/80 hover:text-white hover:scale-[1.05] active:scale-95 transition-all duration-200"
                    >
                      <MdDeleteSweep size={19} />
                    </button>
                  </div>
                </div>
                {matches?.length < 1 && (
                  <div
                    className={`items-center flex flex-col justify-center overflow-auto`}
                  >
                    <div className="flex flex-col items-center justify-center w-1/2">
                      {/* App chrome, not the page heading — an empty-state label. As an <h1>
                          it competed with the page's real <h1> whenever the chat UI rendered
                          server-side, giving /chat two top-level headings. */}
                      <h2 className="text-white text-2xl mb-3 flex items-center justify-center">
                        <FaHeart size={28} className="text-gray-400 mr-2" />
                        {t('chat')}
                      </h2>
                      <h3 className="text-gray-400 text-center text-sm">
                        {t('emptyChatMatchesContent')}
                      </h3>
                    </div>
                  </div>
                )}
                <ul className="max-h-72 overflow-y-auto">
                  {sortedMatches?.map((match, index) => (
                    <MessageButton
                      key={index}
                      match={match}
                      onLinkClick={() => {
                        setCurrentMatch(match)
                        setDiscover(false)
                        setIsOpen(false)
                      }}
                      selected={currentMatch && currentMatch.id === match.id}
                      onClose={() => {
                        if (!user) {
                          setSignUpModalOpen(true)
                          return
                        }
                        setModalOpen(true)
                      }}
                      setCardModalOpen={setCardModalOpen}
                      giftById={giftsEnabled ? giftById : null}
                    />
                  ))}
                </ul>

                <button
                  onClick={() => {
                    if (!user) {
                      setSignUpModalOpen(true)
                      return
                    }
                    setDiscover(true)
                    setCurrentMatch(null)
                    setIsOpen(false)
                  }}
                  className={`w-5/6 mx-auto mt-2.5 flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-semibold text-white bg-[var(--main-bg-color)] shadow-lg shadow-indigo-500/25 transition-all duration-200 hover:scale-[1.02] hover:brightness-110 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
                    !currentMatch && 'opacity-50 pointer-events-none'
                  }`}
                >
                  <TbCards size={20} className="shrink-0" />
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <span>{t('discover')}</span>
                    <span className="hidden md:inline">{t('profiles')}</span>
                  </span>
                </button>

                <button
                  onClick={() => {
                    setCreateModalOpen(true)
                    setIsOpen(false)
                  }}
                  className={`w-5/6 mx-auto mt-2.5 flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-semibold text-white bg-[var(--secondary-bg-color)] transition-all duration-200 hover:scale-[1.02] hover:brightness-125 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--main-bg-color)] ${
                    createModalOpen && 'opacity-50 pointer-events-none'
                  }`}
                >
                  <TbFaceId size={20} className="shrink-0" />
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <span>{t('create')}</span>
                    <span className="hidden md:inline">{t('aCharacter')}</span>
                  </span>
                </button>

                {/* Divider: separates the "you" action from the character actions above. */}
                <div className="w-4/6 mx-auto mt-2.5 border-t border-white/5" />

                <button
                  onClick={() => {
                    if (!user) {
                      setSignUpModalOpen(true)
                      return
                    }
                    setPersonaModalOpen(true)
                    setIsOpen(false)
                  }}
                  className={`w-5/6 mx-auto mt-2.5 flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-gray-300 bg-transparent border border-[var(--secondary-bg-color)] transition-all duration-200 hover:scale-[1.02] hover:bg-[var(--foreground-bg-color)] hover:text-white active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--main-bg-color)] ${
                    personaModalOpen && 'opacity-50 pointer-events-none'
                  }`}
                >
                  <TbUser size={20} className="shrink-0" />
                  <span className="whitespace-nowrap">
                    {t('myPersona', { defaultValue: 'My Persona' })}
                  </span>
                </button>
              </div>
            </div>
          )}

          {!callModalOpen && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white p-4 pr-0 self-start md:hidden absolute flex items-center justify-center"
            >
              <img
                src={
                  currentMatch?.imageUrl?.[0] ||
                  'https://gravatar.com/avatar/3246a02ac0a853fbc05c6d85753ac06a?s=200&d=mp&r=x'
                }
                className="w-8 h-8 object-cover object-top rounded-full mr-2"
              />
              <FaChevronRight className="h-4" />
            </button>
          )}

          {discover ? (
            <div className="md:w-2/3 w-full px-0 py-8 sm:p-8 overflow-y-auto bg-[var(--background-bg-color)] mx-auto">
              <Suspense>
                <Discover />
              </Suspense>
            </div>
          ) : callModalOpen ? (
            <div className="w-full">
              <div className="bg-[var(--background-bg-color)] h-[calc(100dvh-var(--pc-content-top,6rem))] flex justify-center items-center">
                <VoiceRecorder
                  user={user}
                  currentMatch={currentMatch}
                  isRoleplay={isRoleplay}
                  language={i18n?.language}
                  setCallModalOpen={setCallModalOpen}
                  isErrorModalOpen={isErrorModalOpen}
                />
              </div>
            </div>
          ) : (
            <div
              // 100dvh accounts for the browser's own chrome but NOT the
              // on-screen keyboard, which is why the composer ended up behind
              // it. Subtracting the keyboard inset shortens the pane instead,
              // so the message list scrolls and the input stays reachable.
              className="md:w-2/3 w-full flex flex-col h-[calc(100dvh-var(--pc-content-top,6rem)-var(--pc-keyboard-inset,0px))] overflow-hidden bg-[var(--background-bg-color)]"
              style={{ overscrollBehavior: 'contain' }}
            >
              <div className="bg-[var(--background-bg-color)] flex flex-col flex-1 min-h-0 md:mx-8 pt-4">
                <div className="flex items-center md:justify-between justify-end gap-2.5 mr-2">
                  <div className="flex items-center gap-2.5">
                    {currentMatch && (
                      <button
                        type="button"
                        aria-label={t('settings')}
                        onClick={() => {
                          if (!user) {
                            setSignUpModalOpen(true)
                            return
                          }
                          setChatSettingsModalOpen(true)
                        }}
                        className="bg-[var(--foreground-bg-color)] p-2.5 md:px-4 md:py-2.5 rounded-full text-white flex relative transition-transform duration-200 hover:scale-[1.05] font-semibold items-center justify-center"
                      >
                        <FaCog size={18} />
                        <span className="md:block hidden text-xs ml-2">
                          {t('settings')}
                        </span>
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-2.5">
                    <button
                      className="bg-[var(--foreground-bg-color)] p-2.5 md:px-4 md:py-2.5 rounded-full text-white flex relative transition-transform duration-200 hover:scale-[1.05] font-semibold"
                      onClick={useCharacter}
                    >
                      <BsPersonBoundingBox size={18} />
                      <span className="md:block hidden text-xs ml-2">
                        {t('create')}
                      </span>
                    </button>

                    {!currentMatch?.isGroup && (
                      <button
                        className="bg-[var(--foreground-bg-color)] p-2.5 md:px-4 md:py-2.5 rounded-full text-white flex relative transition-transform duration-200 hover:scale-[1.05] font-semibold "
                        onClick={() => {
                          setCallModalOpen(true)
                        }}
                      >
                        <FaPhone size={18} />
                        <span className=" text-xs ml-2">
                          {t('call')}{' '}
                          <span className="font-black text-xxs">v2!</span>
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                <div className="relative w-full mx-auto p-2 px-0 flex-1 min-h-0">
                  {/* Mounted OUTSIDE the chatscope ChatContainer — it drops
                      unknown children, so the ref/preview must live here. */}
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handlePhotoSelect}
                  />
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoSelect}
                  />
                  {chatOutNotice && (
                    <div
                      role="status"
                      className="pointer-events-none absolute inset-x-0 bottom-16 z-20 flex justify-center px-4"
                    >
                      <span className="rounded-full border border-white/10 bg-black/50 px-3 py-1 text-[11px] text-white/60 backdrop-blur-sm">
                        {chatOutNotice}
                      </span>
                    </div>
                  )}
                  {pendingPhoto && (
                    <div className="absolute left-4 bottom-16 z-30 flex items-center gap-2 rounded-xl bg-[#0b0a0f]/90 border border-white/10 px-3 py-2 shadow-lg">
                      <div className="relative">
                        <img
                          src={pendingPhoto}
                          alt="Selected"
                          className="h-14 w-14 object-cover rounded-lg border border-white/10"
                        />
                        <button
                          type="button"
                          onClick={() => setPendingPhoto(null)}
                          className="absolute -top-2 -right-2 bg-[#0b0a0f] text-white rounded-full p-1 border border-white/20"
                          aria-label="Remove photo"
                        >
                          <FaTimes className="text-xs" />
                        </button>
                      </div>
                    </div>
                  )}
                  <MainContainer>
                    <ChatContainer>
                      {currentChat?.length > 0 && (
                        <MessageList
                          typingIndicator={
                            isBotTyping && (
                              <TypingIndicator
                                content={t(
                                  isRoleplay
                                    ? 'characterIsThinking'
                                    : 'characterIsTyping',
                                  {
                                    name: currentMatch?.isGroup
                                      ? typingName || currentMatch?.name
                                      : currentMatch?.name,
                                  },
                                )}
                              />
                            )
                          }
                          className="overflow-auto"
                        >
                          {currentChat
                            .filter((msg) => !msg.hidden) // Add this line to filter out hidden messages
                            .map((msg, index) => {
                              // Image-only messages (a shared/generated photo with
                              // no caption) drop the chat bubble, iMessage-style.
                              const strippedText = (msg?.content || '')
                                .replace(
                                  // "receiving it as you speak" covers gift
                                  // messages written BEFORE the stage direction
                                  // moved into the system prompt — that text is
                                  // already persisted in real chat histories and
                                  // would otherwise stay visible forever.
                                  /\s*\[[^[\]]*(?:you a photo|scene depicts|receiving it as you speak)\b[\s\S]*$/,
                                  '',
                                )
                                .trim()
                              const isImageOnly =
                                !strippedText &&
                                msg?.format !== 'audio' &&
                                !!(msg?.message_photo_url || msg?.message_photo)
                              // A sent gift is the gift's emoji alone — the
                              // reaction instruction lives in the system prompt,
                              // so nothing else is in the bubble. Matching against
                              // the catalog means a user simply typing 🌹 is never
                              // mistaken for a purchase.
                              const isGiftMessage =
                                msg?.role !== 'assistant' &&
                                msg?.format !== 'audio' &&
                                giftEmojiSet.has((msg?.content || '').trim())
                              return (
                                <Message
                                  key={index}
                                  className={
                                    isGiftMessage
                                      ? `cs-message--gift cs-message--gift-${
                                          giftTierByEmoji[
                                            (msg?.content || '').trim()
                                          ] || 'small'
                                        }`
                                      : isImageOnly
                                        ? 'cs-message--image-only'
                                        : undefined
                                  }
                                  // Tapping a gift replays its celebration.
                                  // Message spreads unknown props onto its root
                                  // <section>, so onClick lands there directly.
                                  onClick={
                                    isGiftMessage
                                      ? () =>
                                          setGiftCelebration((prev) => ({
                                            emoji: (msg?.content || '').trim(),
                                            n: (prev?.n || 0) + 1,
                                          }))
                                      : undefined
                                  }
                                  model={{
                                    src: msg?.content,
                                    message: msg?.content || '',
                                    sentTime: msg?.sentTime || 'Just now',
                                    sender: msg?.sender,
                                    direction:
                                      msg?.role === 'assistant'
                                        ? 'incoming'
                                        : 'outgoing',
                                  }}
                                >
                                  <Avatar
                                    src={
                                      msg.role === 'assistant'
                                        ? groupSenderAvatar(msg.sender) ||
                                          'https://gravatar.com/avatar/3246a02ac0a853fbc05c6d85753ac06a?s=200&d=mp&r=x'
                                        : displayProfilePicture ||
                                          profileImage ||
                                          'https://gravatar.com/avatar/3246a02ac0a853fbc05c6d85753ac06a?s=200&d=mp&r=x'
                                    }
                                    name={msg.sender_name || msg.name}
                                    onClick={() => {
                                      if (msg.role === 'assistant') {
                                        setCardModalOpen(true)
                                      }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  />

                                  {
                                    msg && // Check if there is a message
                                      (msg.format === 'audio' ? (
                                        // If the format is audio, show the Play button

                                        <Message.CustomContent>
                                          <div>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                playAudio(msg?.content)
                                              }
                                              className="px-4 py-2 rounded-full focus:outline-none text-white bg-[#403D4A] font-semibold"
                                            >
                                              <FaPlay />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={pauseAudio}
                                              className="px-4 py-2 rounded-full focus:outline-none text-white bg-[#403D4A] font-semibold ml-1"
                                            >
                                              <FaPause />
                                            </button>
                                          </div>
                                        </Message.CustomContent>
                                      ) : isUrl(msg.content) ? ( // Check if the content is a URL for images
                                        // If the content is a URL and not audio, assume it's an image and show it
                                        <Message.ImageContent
                                          src={msg.content}
                                          alt="Chat Image"
                                          className={`${
                                            !user && 'blur'
                                          } cursor-pointer md:max-w-[300px]`}
                                        />
                                      ) : null) // If neither, don't render anything
                                  }

                                  <Message.CustomContent>
                                    <div className="flex flex-col">
                                      {/* In a group, label each character's bubble
                                        with their name so it's clear who's speaking. */}
                                      {currentMatch?.isGroup &&
                                        msg.role === 'assistant' &&
                                        msg.sender_name && (
                                          <span className="text-xs font-semibold text-[var(--main-bg-color)] mb-0.5">
                                            {msg.sender_name}
                                          </span>
                                        )}
                                      {/* Photo the user shared. The image itself is
                                        NEVER stored — it only lives in memory this
                                        session (optimistic data URL). On reload we
                                        just show a small placeholder chip. */}
                                      {msg?.message_photo_url ? (
                                        <div className="rounded-xl overflow-hidden mb-2 w-full md:max-w-[300px]">
                                          <img
                                            src={msg.message_photo_url}
                                            alt="Shared photo"
                                            onClick={() =>
                                              user &&
                                              setFullSrc(msg.message_photo_url)
                                            }
                                            className={`${
                                              !user ? 'blur' : 'cursor-pointer'
                                            } rounded-lg w-full h-auto`}
                                          />
                                        </div>
                                      ) : (
                                        /\[[^[\]]*(?:you a photo|scene depicts)\b/.test(
                                          msg?.content || '',
                                        ) && (
                                          <div className="inline-flex items-center gap-1 text-xs text-gray-400 mb-1">
                                            <FaCamera />{' '}
                                            {t('photo', {
                                              defaultValue: 'Photo',
                                            })}
                                          </div>
                                        )
                                      )}
                                      {editingMessage === msg ? (
                                        <div className="flex w-full min-w-[15rem] flex-col gap-2">
                                          <textarea
                                            autoFocus
                                            rows={2}
                                            value={editText}
                                            onFocus={(e) => {
                                              // Put the caret at the end, not the start.
                                              const len = e.target.value.length
                                              e.target.setSelectionRange(
                                                len,
                                                len,
                                              )
                                            }}
                                            onChange={(e) =>
                                              setEditText(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (
                                                e.key === 'Enter' &&
                                                !e.shiftKey
                                              ) {
                                                e.preventDefault()
                                                handleSubmitEdit(msg)
                                              }
                                              if (e.key === 'Escape') {
                                                setEditingMessage(null)
                                                setEditText('')
                                              }
                                            }}
                                            className="w-full resize-none bg-transparent text-sm leading-relaxed text-gray-100 focus:outline-none"
                                          />
                                          <div className="flex justify-end gap-2">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEditingMessage(null)
                                                setEditText('')
                                              }}
                                              className="rounded-full border border-white/20 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
                                            >
                                              {t('cancel', {
                                                defaultValue: 'Cancel',
                                              })}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleSubmitEdit(msg)
                                              }
                                              disabled={
                                                !editText.trim() || isLoading
                                              }
                                              className="rounded-full bg-white px-4 py-1.5 text-sm font-medium text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40"
                                            >
                                              {isLoading ? (
                                                <FaSpinner className="animate-spin" />
                                              ) : (
                                                t('send', {
                                                  defaultValue: 'Send',
                                                })
                                              )}
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        processMessageContent(
                                          (msg?.content || '')
                                            .replace(
                                              /\s*\[[^[\]]*(?:you a photo|scene depicts)\b[\s\S]*$/,
                                              '',
                                            )
                                            .trim(),
                                        ).map((part, index) => {
                                          switch (part.type) {
                                            case 'narration':
                                              return (
                                                <p
                                                  key={index}
                                                  className="
                                                leading-relaxed
                                                text-gray-300 italic
                                                font-normal
                                                tracking-[0.01em]
                                              "
                                                >
                                                  {part.content}
                                                </p>
                                              )

                                            case 'dialogue':
                                              return (
                                                <p
                                                  key={index}
                                                  className="
                                               leading-relaxed
                                                text-gray-100
                                                font-semibold
                                                italic
                                                tracking-[0.015em]
                                              "
                                                >
                                                  {part.content}
                                                </p>
                                              )

                                            default: // 'chat'
                                              return (
                                                <p
                                                  key={index}
                                                  className="
                                          leading-relaxed
                                                text-gray-200
                                                font-normal
                                              "
                                                >
                                                  {part.content}
                                                </p>
                                              )
                                          }
                                        })
                                      )}

                                      {(msg?.message_photo ||
                                        msg?.message_video) && (
                                        <div className="relative group w-full md:max-w-[300px]">
                                          {/* Ellipsis menu (top-right) holds every
                                            image action, like the /generate feed.
                                            Overflow isn't clipped (no overflow-hidden
                                            on the wrapper) so the dropdown can show. */}
                                          <div
                                            className={`chat-image-actions absolute top-3 right-3 z-20 flex items-center transition-opacity duration-200 ${
                                              openImageMenuMsg === msg
                                                ? 'opacity-100'
                                                : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'
                                            }`}
                                          >
                                            <OverlayActionsMenu
                                              onOpenChange={(open) =>
                                                setOpenImageMenuMsg(
                                                  open ? msg : null,
                                                )
                                              }
                                              items={[
                                                // A clip is already animated, and "save
                                                // file" here writes a jpeg from base64 —
                                                // neither applies to a video, so both drop
                                                // out rather than appearing and failing.
                                                ...(msg?.message_video
                                                  ? []
                                                  : [
                                                      {
                                                        key: 'animate',
                                                        icon: (
                                                          <FaFilm className="text-sm" />
                                                        ),
                                                        label: t('animate'),
                                                        onClick: () =>
                                                          useAnimate(
                                                            msg,
                                                            index,
                                                          ),
                                                      },
                                                      {
                                                        key: 'save',
                                                        icon: (
                                                          <FaSave className="text-sm" />
                                                        ),
                                                        label: t('saveFile'),
                                                        onClick: () =>
                                                          saveImage(
                                                            `data:image/jpeg;base64,${msg.message_photo}`,
                                                            `image_${msg.id ?? 'photo'}.jpg`,
                                                          ),
                                                      },
                                                    ]),
                                                {
                                                  key: 'publish',
                                                  icon: (
                                                    <FaGlobeAmericas className="text-sm" />
                                                  ),
                                                  label: t('publish'),
                                                  onClick: () => {
                                                    setPublishTarget({
                                                      msg,
                                                      index,
                                                    })
                                                    setAcceptedPublishTerms(
                                                      false,
                                                    )
                                                    setShowTermsError(false)
                                                    setPublishVisibility(
                                                      'Private',
                                                    )
                                                    setPublishModalOpen(true)
                                                  },
                                                },
                                                {
                                                  key: 'delete',
                                                  icon: (
                                                    <FaTrash className="text-sm" />
                                                  ),
                                                  label: t('deleteImage', {
                                                    defaultValue:
                                                      'Delete image',
                                                  }),
                                                  destructive: true,
                                                  onClick: () =>
                                                    handleDeleteChatImage(msg),
                                                },
                                              ]}
                                            />
                                          </div>

                                          {msg?.message_video ? (
                                            /* A scene clip is a hosted URL, not base64
                                              like message_photo. Autoplay follows the
                                              same rules as the /generate feed's Card:
                                              muted + loop + playsInline, because every
                                              browser blocks autoplay WITH sound and iOS
                                              additionally needs playsInline to avoid
                                              going fullscreen. `controls` is what lets
                                              the user unmute — which matters here more
                                              than on a Card, since the whole point of
                                              this model is that the character speaks. */
                                            <video
                                              src={msg.message_video}
                                              className="w-full h-auto rounded-xl"
                                              autoPlay={chatVideoAutoplay}
                                              loop
                                              muted
                                              controls
                                              playsInline
                                              preload="metadata"
                                            />
                                          ) : (
                                            <img
                                              src={`data:image/jpeg;base64,${msg.message_photo}`}
                                              onClick={() =>
                                                setFullSrc(
                                                  `data:image/jpeg;base64,${msg.message_photo}`,
                                                )
                                              }
                                              className="w-full h-auto rounded-xl cursor-pointer"
                                            />
                                          )}
                                        </div>
                                      )}

                                      {msg?.content &&
                                        !isGiftMessage &&
                                        editingMessage !== msg && (
                                          <div className="self-end flex mt-2">
                                            {/* A gift has no actions: editing it
                                              would let the emoji be rewritten
                                              into arbitrary text after it was
                                              paid for, and reading a single
                                              emoji aloud is meaningless. */}
                                            {/* Edit: text-only user messages only.
                                              Works in groups too — rollback +
                                              regen is group-aware. */}
                                            {msg?.role === 'user' &&
                                              !msg?.message_photo &&
                                              !msg?.message_photo_url &&
                                              msg?.format !== 'audio' &&
                                              !editingMessage && (
                                                <button
                                                  type="button"
                                                  title={t('edit', {
                                                    defaultValue: 'Edit',
                                                  })}
                                                  onClick={() => {
                                                    setEditingMessage(msg)
                                                    setEditText(
                                                      msg?.content || '',
                                                    )
                                                  }}
                                                  disabled={isLoading}
                                                  className="mx-1 rounded-full bg-white/10 px-2 py-2 font-semibold text-[var(--shade-bg-color)] transition-colors hover:bg-white/20 hover:text-white focus:outline-none disabled:opacity-50"
                                                >
                                                  <FaPencilAlt />
                                                </button>
                                              )}
                                            {msg?.narration_clip ||
                                            msg?.format === 'audio' ? (
                                              <>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    playAudio(
                                                      msg?.narration_clip,
                                                    )
                                                  }
                                                  className={`px-4 py-2 rounded-full focus:outline-none font-semibold ${
                                                    msg?.role === 'assistant'
                                                      ? 'text-white bg-white/10 '
                                                      : 'text-[var(--shade-bg-color)] bg-white/10 '
                                                  }`}
                                                >
                                                  <FaPlay />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={pauseAudio}
                                                  className={`px-4 py-2 rounded-full focus:outline-none font-semibold ml-1 ${
                                                    msg?.role === 'assistant'
                                                      ? 'text-white bg-white/10 '
                                                      : 'text-[var(--shade-bg-color)] bg-white/10 '
                                                  }`}
                                                >
                                                  <FaPause />
                                                </button>
                                              </>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  generateNarration(
                                                    msg?.content,
                                                  )
                                                }
                                                className={`px-2 py-2 rounded-full focus:outline-none font-semibold mx-1 ${
                                                  msg?.role === 'assistant'
                                                    ? 'text-white bg-white/10 '
                                                    : 'text-[var(--shade-bg-color)] bg-white/10 '
                                                }`}
                                              >
                                                {isNarrationLoading &&
                                                currentLoadingNarration ===
                                                  msg?.content ? (
                                                  <FaSpinner className="animate-spin" />
                                                ) : (
                                                  <HiSpeakerWave />
                                                )}
                                              </button>
                                            )}

                                            {['v2', 'v3'].includes(
                                              currentMatch?.chat_model,
                                            ) &&
                                              msg?.role === 'assistant' &&
                                              msg?.content ===
                                                currentChat?.slice(-1)[0]
                                                  ?.content && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const lastUserMessage = [
                                                      ...(currentChat || []),
                                                    ]
                                                      .reverse()
                                                      .find(
                                                        (message) =>
                                                          message.role ===
                                                          'user',
                                                      )

                                                    sendMessage(
                                                      lastUserMessage?.content,
                                                      msg?.send_date,
                                                      null,
                                                      true,
                                                    )
                                                  }}
                                                  className={`px-2 py-2 rounded-full focus:outline-none font-semibold mx-1 disabled:opacity-50 ${
                                                    msg?.role === 'assistant'
                                                      ? 'text-white bg-white/10 '
                                                      : 'text-[var(--shade-bg-color)] bg-white/10 '
                                                  }`}
                                                  disabled={isLoading}
                                                >
                                                  {isLoading &&
                                                  msg?.send_date ===
                                                    currentSentDate ? (
                                                    <FaSpinner className="animate-spin" />
                                                  ) : (
                                                    <FaSync />
                                                  )}
                                                </button>
                                              )}
                                          </div>
                                        )}
                                    </div>
                                  </Message.CustomContent>
                                </Message>
                              )
                            })}
                        </MessageList>
                      )}

                      <MessageInput
                        placeholder={t('typeAMessage')}
                        onSend={(text) => sendMessage(text, null, null)}
                        sendOnReturnDisabled={false}
                        autoFocus={false}
                        value={message}
                        onChange={(text) => {
                          setMessage(text)
                        }}
                        onAttachClick={() => {
                          const ua =
                            typeof navigator !== 'undefined'
                              ? navigator.userAgent || ''
                              : ''
                          const isMobile =
                            /Android|iPhone|iPad|iPod/i.test(ua) ||
                            (navigator?.maxTouchPoints > 1 &&
                              /Macintosh/.test(ua))
                          if (isMobile) {
                            setPhotoSourceModalOpen(true)
                          } else {
                            photoInputRef.current?.click()
                          }
                        }}
                        onPaste={(event) => {
                          event.preventDefault() // Prevent the default paste action
                          const plainText =
                            event.clipboardData.getData('text/plain') // Get plain text
                          document.execCommand('insertText', false, plainText) // Insert plain text into the input
                        }}
                      />
                    </ChatContainer>
                  </MainContainer>
                </div>
              </div>

              <div
                // The pane is already sized to 100dvh, which stops ABOVE the
                // browser's own toolbar — and iOS still reports a ~34px
                // safe-area-inset-bottom for the area that toolbar is sitting
                // in. Adding it here stacked a second, empty gap under the
                // action row (~50px of dead space on an iPhone). The inset is
                // only ours to clear when the page owns the full screen, i.e.
                // an installed/standalone launch, so it is scoped to that.
                className="shrink-0 flex flex-col items-center gap-2 pt-0.5 pb-2 [@media(display-mode:standalone)]:pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
              >
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      currentMatch?.isGroup
                        ? generateGroupImage()
                        : generateImage()
                    }
                    disabled={isImageLoading}
                    className="
                    group inline-flex items-center justify-center gap-1.5
                    min-w-[88px] h-[36px]
                    rounded-full border border-white/10 bg-[var(--main-bg-color)]
                    px-3 text-xs font-semibold text-white
                    shadow-sm transition-all duration-200
                    hover:scale-[1.03]
                    disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100
                  "
                  >
                    <span className="flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                      {isImageLoading ? (
                        <FaSpinner className="animate-spin" size={14} />
                      ) : (
                        <TbPhotoSpark size={14} />
                      )}
                    </span>

                    <span className="whitespace-nowrap">
                      {isImageLoading ? 'Imagining…' : 'Image'}
                    </span>
                    {!isImageLoading && (
                      <span className="flex items-center gap-1 opacity-60 font-normal">
                        <FaGem size={9} />
                        {chatImageGemCost(
                          currentMatch?.isGroup,
                          aiCharacterVersion,
                        )}
                      </span>
                    )}
                  </button>

                  {!currentMatch?.isGroup && (
                    <button
                      type="button"
                      onClick={generateSceneVideo}
                      disabled={isVideoLoading || isImageLoading}
                      className="
                      group inline-flex items-center justify-center gap-1.5
                      min-w-[88px] h-[36px]
                      rounded-full border border-white/10 bg-[var(--foreground-bg-color)]
                      px-3 text-xs font-semibold text-white
                      shadow-sm transition-all duration-200
                      hover:scale-[1.03]
                      disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100
                    "
                    >
                      <span className="flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                        {isVideoLoading ? (
                          <FaSpinner className="animate-spin" size={14} />
                        ) : (
                          <FaFilm size={14} />
                        )}
                      </span>
                      <span className="whitespace-nowrap">
                        {isVideoLoading
                          ? videoStatusText || 'Filming…'
                          : 'Video'}
                      </span>
                      {!isVideoLoading && (
                        <span className="flex items-center gap-1 opacity-60 font-normal">
                          <FaGem size={9} />
                          {chatVideoGemCost}
                        </span>
                      )}
                    </button>
                  )}

                  {/* Companion gifts A/B. Sits in the same action row as Create
                      Image / Create Video and borrows their exact styling, so it
                      reads as a peer action rather than a bolted-on promo. Hidden
                      entirely for the control arm (giftsEnabled is false there). */}
                  {giftsEnabled &&
                    !currentMatch?.isGroup &&
                    currentMatch?.id && (
                      <button
                        type="button"
                        onClick={() => setGiftShopOpen(true)}
                        aria-label="Send a gift"
                        className="
                      group inline-flex items-center justify-center gap-1.5
                      min-w-[88px] h-[36px]
                      rounded-full border border-white/10 bg-[var(--foreground-bg-color)]
                      px-3 text-xs font-semibold text-white
                      shadow-sm transition-all duration-200
                      hover:scale-[1.03]
                      disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100
                    "
                      >
                        <span className="flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                          <FaGift size={14} />
                        </span>
                        <span className="whitespace-nowrap">Gift</span>
                      </button>
                    )}

                  {currentMatch?.isGroup && currentChat?.length > 0 && (
                    <button
                      type="button"
                      onClick={continueGroup}
                      disabled={isLoading}
                      className="
                      group inline-flex items-center justify-center gap-1.5
                      min-w-[88px] h-[36px]
                      rounded-full border border-white/10 bg-[var(--foreground-bg-color)]
                      px-3 text-xs font-semibold text-white
                      shadow-sm transition-all duration-200
                      hover:scale-[1.03]
                      disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100
                    "
                    >
                      <span className="flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                        {isLoading ? (
                          <FaSpinner className="animate-spin" size={14} />
                        ) : (
                          <TbUsersGroup size={14} />
                        )}
                      </span>
                      <span className="whitespace-nowrap">
                        {t('letThemContinue', {
                          defaultValue: 'Let them keep talking',
                        })}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Companion gifts A/B. Renders nothing unless the shop reported enabled,
          so the control arm sees no change at all. After a successful gift we
          send the gift emoji as the user's turn: sendMessage() requires some
          content, and it reads naturally in the transcript (the server appends
          the in-character reaction note to that same turn). */}
      {giftCelebration && (
        <GiftConfetti
          key={giftCelebration.n}
          emoji={giftCelebration.emoji}
          onDone={() => setGiftCelebration(null)}
        />
      )}

      {giftsEnabled && currentMatch?.id && (
        <GiftShop
          open={giftShopOpen}
          onClose={() => setGiftShopOpen(false)}
          user={user}
          characterId={currentMatch?.id}
          characterName={currentMatch?.name}
          gifts={giftCatalog}
          sections={giftSections}
          gems={giftGems}
          owned={ownedGifts}
          onNeedGems={(gift) => {
            // Reuse the app's existing insufficient-gems path rather than
            // inventing a gift-specific one: the shared limit modal already
            // routes paid plans to /gems and everyone else to /upgrade.
            setLimitTitle(
              `\uD83D\uDC8E ${t('notEnoughGems', {
                defaultValue: 'Not enough gems',
              })}`,
            )
            setErrorText(
              <div className="text-center">
                {t('notEnoughGemsForGift', {
                  gift: gift?.name,
                  gems: gift?.gems,
                  defaultValue: `You need ${gift?.gems} gems to send ${gift?.name}.`,
                })}
              </div>,
            )
            setIsLimitModalOpen(true)
          }}
          onGifted={(gift) => {
            refreshGifts()
            setGiftCelebration((prev) => ({
              emoji: gift.emoji,
              n: (prev?.n || 0) + 1,
            }))
            sendMessage(gift.emoji, null, null)
          }}
        />
      )}
    </>
  )
}

export default ChatComponent
